import { Observable, Subject, BehaviorSubject, merge } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

/*
MIT License

Copyright (c) 2020 Ankit Singh <dabalyan@hotmail.com>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

This license applies to the complete mono-repo, unless specified otherwise.
*/

// ____________________________ Common Events ____________________________ //
// _______________________________________________________________________ //
/**
 * An event that gets emitted on successful replay by using the `replay` method.
 * @event
 * @category Common
 */
class EventReplay {
    /**
     * @param value The current value that got replayed.
     */
    constructor(value) {
        this.value = value;
    }
}
// _________________________ Common Units Events _________________________ //
// _______________________________________________________________________ //
/**
 * An event that gets emitted on successful dispatch by using the Units' `dispatch` method.
 * @event
 * @category Common Units
 */
class EventUnitDispatch {
    /**
     * @param value The value that was passed to the dispatch method.
     * @param options The options that were passed to the dispatch method.
     */
    constructor(value, options) {
        this.value = value;
        this.options = options;
    }
}
/**
 * All the reasons for why a Unit dispatch might fail.
 */
var DispatchFailReason;
(function (DispatchFailReason) {
    /**
     * The first reason, if the Unit is frozen.
     */
    DispatchFailReason["FROZEN_UNIT"] = "FROZEN_UNIT";
    /**
     * The second reason, if the dispatched value is invalid.
     */
    DispatchFailReason["INVALID_VALUE"] = "INVALID_VALUE";
    /**
     * The third reason, if {@link UnitConfig.customDispatchCheck} returns a `falsy` value.
     */
    DispatchFailReason["CUSTOM_DISPATCH_CHECK"] = "CUSTOM_DISPATCH_CHECK";
    /**
     * The fourth reason, if {@link UnitConfig.distinctDispatchCheck} is not `false` and the dispatched value is same as current value.
     */
    DispatchFailReason["DISTINCT_CHECK"] = "DISTINCT_CHECK";
})(DispatchFailReason || (DispatchFailReason = {}));
/**
 * An event that gets emitted on failed dispatch using the Units' `dispatch` method.
 * @event
 * @category Common Units
 */
class EventUnitDispatchFail {
    /**
     * @param value The value that was passed to the dispatch method.
     * @param reason The reason for why the dispatch failed.
     * @param options The options that were passed to the dispatch method.
     */
    constructor(value, reason, options) {
        this.value = value;
        this.reason = reason;
        this.options = options;
    }
}
/**
 * An event that gets emitted on successful unmute using the Units' `unmute` method.
 * @event
 * @category Common Units
 */
class EventUnitUnmute {
}
/**
 * An event that gets emitted when a Unit gets frozen.
 * @event
 * @category Common Units
 */
class EventUnitFreeze {
}
/**
 * An event that gets emitted when a Unit gets unfrozen after being frozen.
 * @event
 * @category Common Units
 */
class EventUnitUnfreeze {
}
/**
 * An event that gets emitted on successful cache-navigation,
 * using the Units' several cache-navigation methods like `goBack`, `goForward`, `jump`, etc.
 * @event
 * @category Common Units
 */
class EventUnitJump {
    /**
     * @param steps The number of steps jumped represented as a number,
     * positive for forward navigation and negative for backwards.
     * @param newCacheIndex The new `cacheIndex` of the emitted value.
     */
    constructor(steps, newCacheIndex) {
        this.steps = steps;
        this.newCacheIndex = newCacheIndex;
    }
}
/**
 * An event that gets emitted on successful execution of Units' `clearCache` method.
 * @event
 * @category Common Units
 */
class EventUnitClearCache {
    /**
     * @param options The options that were directly or indirectly passed to the `clearCache` method.
     */
    constructor(options) {
        this.options = options;
    }
}
/**
 * An event that gets emitted on successful execution of Units' `clearValue` method.
 * @event
 * @category Common Units
 */
class EventUnitClearValue {
}
/**
 * An event that gets emitted on successful execution of Units' `clear` method.
 * @event
 * @category Common Units
 */
class EventUnitClear {
    /**
     * @param options The options that were passed for the implicitly called `clearCache` method.
     */
    constructor(options) {
        this.options = options;
    }
}
/**
 * An event that gets emitted on successful execution of Units' `resetValue` method.
 * @event
 * @category Common Units
 */
class EventUnitResetValue {
}
/**
 * An event that gets emitted on successful execution of Units' `reset` method.
 * @event
 * @category Common Units
 */
class EventUnitReset {
    /**
     * @param options The options that were passed for the implicitly called `clearCache` method.
     */
    constructor(options) {
        this.options = options;
    }
}
/**
 * An event that gets emitted on successful execution of Units' `clearPersistentValue` method.
 * @event
 * @category Common Units
 */
class EventUnitClearPersistedValue {
}
// ____________________________ DictUnit Events ____________________________ //
// _________________________________________________________________________ //
/**
 * An event that gets emitted on successful execution of DictUnit's `set` method.
 * @event
 * @category DictUnit
 */
class EventDictUnitSet {
    /**
     * @param key The name of the property that was passed to the `set` method.
     * @param value The value of the property that was passed to the `set` method.
     */
    constructor(key, value) {
        this.key = key;
        this.value = value;
    }
}
/**
 * An event that gets emitted on successful execution of DictUnit's `assign` method.
 * @event
 * @category DictUnit
 */
class EventDictUnitAssign {
    /**
     * @param sources The source objects that were passed to the `assign` method.
     * @param newProps The new properties that finally got added to the DictUnit's value.
     */
    constructor(sources, newProps) {
        this.sources = sources;
        this.newProps = newProps;
    }
}
/**
 * An event that gets emitted on successful execution of DictUnit's `delete` or `deleteIf` method.
 * @event
 * @category DictUnit
 */
class EventDictUnitDelete {
    /**
     * @param deletedProps The properties that were deleted by the `delete` or `deleteIf` method.
     */
    constructor(deletedProps) {
        this.deletedProps = deletedProps;
    }
}
// ____________________________ ListUnit Events ____________________________ //
// _________________________________________________________________________ //
/**
 * An event that gets emitted on successful execution of ListUnit's `set` method.
 * @event
 * @category ListUnit
 */
class EventListUnitSet {
    /**
     * @param index The index for the item passed to the `set` method.
     * @param item The item passed to the `set` method.
     */
    constructor(index, item) {
        this.index = index;
        this.item = item;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `pop` method.
 * @event
 * @category ListUnit
 */
class EventListUnitPop {
    /**
     * @param item The item that got popped from the ListUnit's value.
     */
    constructor(item) {
        this.item = item;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `push` method.
 * @event
 * @category ListUnit
 */
class EventListUnitPush {
    /**
     * @param items The items that were passed to the `push` method.
     */
    constructor(items) {
        this.items = items;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `shift` method.
 * @event
 * @category ListUnit
 */
class EventListUnitShift {
    /**
     * @param item The item that got shifted out.
     */
    constructor(item) {
        this.item = item;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `unshift` method.
 * @event
 * @category ListUnit
 */
class EventListUnitUnshift {
    /**
     * @param items The items that were passed to the `unshift` method.
     */
    constructor(items) {
        this.items = items;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `delete` or `deleteIf` method.
 * @event
 * @category ListUnit
 */
class EventListUnitDelete {
    /**
     * @param indices The indices that were passed to the `delete` method explicitly,
     * or implicitly by `deleteIf` method.
     * @param deletedItems The items that got deleted from the ListUnit's value.
     */
    constructor(indices, deletedItems) {
        this.indices = indices;
        this.deletedItems = deletedItems;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `remove` or `removeIf` method.
 * @event
 * @category ListUnit
 */
class EventListUnitRemove {
    /**
     * @param indices The indices that were passed to the `remove` method explicitly,
     * or implicitly by `removeIf` method.
     * @param removedItems The items that got removed from the ListUnit's value.
     */
    constructor(indices, removedItems) {
        this.indices = indices;
        this.removedItems = removedItems;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `splice` or `insert` method.
 * @event
 * @category ListUnit
 */
class EventListUnitSplice {
    /**
     * @param start The zero-based location that was passed to the `splice` method.
     * @param deleteCount The number of items that were to be removed.
     * @param removedItems The items that got removed.
     * @param addedItems The items that were passed as `items` to be added.
     */
    constructor(start, deleteCount, removedItems, addedItems) {
        this.start = start;
        this.deleteCount = deleteCount;
        this.removedItems = removedItems;
        this.addedItems = addedItems;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `fill` method.
 * @event
 * @category ListUnit
 */
class EventListUnitFill {
    /**
     * @param item The item that was passed to the `fill` method.
     * @param start The starting position where the filling started.
     * @param end The last position where the filling stopped.
     */
    constructor(item, start, end) {
        this.item = item;
        this.start = start;
        this.end = end;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `copyWithin` method.
 * @event
 * @category ListUnit
 */
class EventListUnitCopyWithin {
    /**
     * @param target The target position from where the copied section starts replacing.
     * @param start The starting position of the copied section.
     * @param end The ending position of the copied section.
     */
    constructor(target, start, end) {
        this.target = target;
        this.start = start;
        this.end = end;
    }
}
/**
 * An event that gets emitted on successful execution of ListUnit's `reverse` method.
 * @event
 * @category ListUnit
 */
class EventListUnitReverse {
}
/**
 * An event that gets emitted on successful execution of ListUnit's `sort` method.
 * @event
 * @category ListUnit
 */
class EventListUnitSort {
}

/**
 * @internal please do not use.
 */
const NOOP = () => { };
/**
 * @internal please do not use.
 */
const IteratorSymbol = (typeof Symbol === 'function' && Symbol.iterator) ||
    /* istanbul ignore next */ '@@iterator';
/**
 * @internal please do not use.
 */
function isValidId(id) {
    return typeof id === 'string' && !!id.trim().length;
}
/**
 * @internal please do not use.
 */
function isDict(o) {
    return Object.prototype.toString.call(o) === '[object Object]';
}
/**
 * @internal please do not use.
 */
function isObject(o) {
    return o != null && typeof o === 'object';
}
/**
 * @internal please do not use.
 */
function isValidKey(key) {
    return typeof key === 'string' || typeof key === 'number';
}
/**
 * @internal please do not use.
 */
function isValidIndex(i) {
    const a = [];
    a[i] = 1;
    return !!a.length && a[i] === 1;
}
/**
 * @internal please do not use.
 */
function normalizeIndex(index, arrLength) {
    return index < 0 ? (index < -arrLength ? 0 : arrLength + index) : index;
}
/**
 * @internal please do not use.
 */
function sanitizeIndices(indices, arrLength) {
    const sanitizedIndices = [];
    indices.forEach(index => {
        index = normalizeIndex(index, arrLength);
        if (index < arrLength && isValidIndex(index)) {
            sanitizedIndices.push(index);
        }
    });
    return deDuplicate(sanitizedIndices);
}
/**
 * @internal please do not use.
 */
function isNumber(n) {
    return typeof n === 'number' && !isNaN(n);
}
/**
 * @internal please do not use.
 */
// tslint:disable-next-line:ban-types
function isFunction(fn) {
    return typeof fn === 'function';
}
/**
 * @internal please do not use.
 */
/*export function isNativeFn(fn: any): fn is () => any {
  return /{\s*?\[native code]\s*?}/.test('' + fn);
}*/
/**
 * Creates a clone of the provided value.\
 * All the primitives are returned as is, since they are immutable.\
 * Non-primitives that this function can clone are array and object-literal.\
 * Other non-primitives are returned as is.
 *
 * This function is also used internally by ActiveJS.
 *
 * @param o The value to be cloned.
 * @returns A clone of the provided value.
 *
 * @category Global
 */
function deepCopy(o) {
    if (o == null || typeof o !== 'object') {
        return o;
    }
    if (Array.isArray(o)) {
        return o.map(v => deepCopy(v));
    }
    if (isDict(o)) {
        return Object.keys(o).reduce((newO, k) => {
            newO[k] = deepCopy(o[k]);
            return newO;
        }, {});
    }
    return o;
}
/**
 * @internal please do not use.
 */
function deepFreeze(o) {
    if (!isObject(o)) {
        return o;
    }
    if (Array.isArray(o)) {
        o.forEach(v => deepFreeze(v));
    }
    else if (isDict(o)) {
        Object.keys(o).forEach(k => deepFreeze(o[k]));
    }
    try {
        return Object.freeze(o);
    }
    catch (e) {
        return o;
    }
}
/**
 * @internal please do not use.
 */
function deDuplicate(arr) {
    if (typeof Set === 'function') {
        return [...new Set(arr)];
    }
    else {
        return arr.filter((x, i) => arr.indexOf(x) === i);
    }
}
/**
 * @internal please do not use.
 */
function isSerializable(o) {
    if (o == null || typeof o === 'string' || typeof o === 'boolean' || typeof o === 'number') {
        return [true];
    }
    /*if (typeof o === 'number') {
      return o === Infinity || o === -Infinity ? [false, o] : [true];
    }*/
    if (Array.isArray(o)) {
        let foundPositive;
        o.find(v => {
            const testResult = isSerializable(v);
            if (testResult[0] === false) {
                foundPositive = testResult;
                return true;
            }
        });
        return foundPositive || [true];
    }
    else if (o.constructor === Object) {
        let foundPositive;
        Object.keys(o).find(k => {
            const testResult = isSerializable(o[k]);
            if (testResult[0] === false) {
                foundPositive = testResult;
                return true;
            }
        });
        return foundPositive || [true];
    }
    return [false, o];
}
/**
 * @internal please do not use.
 */
function findIndex(array, predicate, fromIndex) {
    let i = isValidIndex(fromIndex) ? Math.max(0, Math.min(fromIndex, array.length - 1)) : 0;
    while (i < array.length) {
        if (predicate(array[i], i, array)) {
            return i;
        }
        ++i;
    }
    return -1;
}
/**
 * @internal please do not use.
 */
function findIndexBackwards(array, predicate, fromIndex) {
    let i = isValidIndex(fromIndex)
        ? Math.max(0, Math.min(fromIndex, array.length - 1))
        : array.length - 1;
    while (i > -1) {
        if (predicate(array[i], i, array)) {
            return i;
        }
        --i;
    }
    return -1;
}
/**
 * @internal please do not use.
 */
function debounce(func, waitTime, callMode) {
    if (!isNumber(waitTime)) {
        waitTime = 200;
    }
    if (!['START', 'END', 'BOTH'].includes(callMode)) {
        callMode = 'END';
    }
    let timeout;
    return function (...args) {
        const context = this;
        const later = () => {
            timeout = null;
            if (callMode !== 'START') {
                return func.apply(context, args);
            }
        };
        const callNow = callMode !== 'END' && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, waitTime);
        if (callNow) {
            return func.apply(context, args);
        }
    };
}
/**
 * @internal please do not use.
 */
function makeNonEnumerable(o) {
    if (o == null || typeof o !== 'object') {
        return;
    }
    Object.keys(o).forEach(key => {
        Object.defineProperty(o, key, {
            enumerable: false,
        });
    });
}
/**
 * @internal please do not use.
 */
function generateAsyncSystemIds(systemId, queryConfig, dataConfig, errorConfig, pendingConfig) {
    var _a, _b, _c, _d;
    const ids = Object.assign(Object.assign(Object.assign(Object.assign({}, ((queryConfig === null || queryConfig === void 0 ? void 0 : queryConfig.hasOwnProperty('id')) && { queryUnitId: queryConfig.id })), ((dataConfig === null || dataConfig === void 0 ? void 0 : dataConfig.hasOwnProperty('id')) && { dataUnitId: dataConfig.id })), ((errorConfig === null || errorConfig === void 0 ? void 0 : errorConfig.hasOwnProperty('id')) && { errorUnitId: errorConfig.id })), ((pendingConfig === null || pendingConfig === void 0 ? void 0 : pendingConfig.hasOwnProperty('id')) && { pendingUnitId: pendingConfig.id }));
    if (isValidId(systemId)) {
        ids.queryUnitId = (_a = ids.queryUnitId) !== null && _a !== void 0 ? _a : systemId + '_QUERY';
        ids.dataUnitId = (_b = ids.dataUnitId) !== null && _b !== void 0 ? _b : systemId + '_DATA';
        ids.errorUnitId = (_c = ids.errorUnitId) !== null && _c !== void 0 ? _c : systemId + '_ERROR';
        ids.pendingUnitId = (_d = ids.pendingUnitId) !== null && _d !== void 0 ? _d : systemId + '_PENDING';
    }
    return ids;
}
/**
 * @internal please do not use.
 */
function plucker(o, path) {
    const length = Array.isArray(path) ? path.length : 0;
    for (let i = 0; i < length; i++) {
        if (o == null) {
            return undefined;
        }
        o = Object.prototype.hasOwnProperty.call(o, path[i]) ? o[path[i]] : undefined;
    }
    return o;
}
/**
 * @internal please do not use.
 */
function hashCode(str) {
    // tslint:disable:no-bitwise
    let hash = 0;
    const length = typeof str === 'string' ? str.length : 0;
    if (length === 0) {
        return String(hash);
    }
    for (let i = 0; i < length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0; // Convert to 32bit integer
    }
    hash = hash >>> 0;
    return Number(hash).toString(32).toUpperCase();
    // tslint:enable:no-bitwise
}
/**
 * @internal please do not use.
 */
function stackTrace() {
    try {
        throw new Error();
    }
    catch (error) {
        return error.stacktrace || error.stack;
    }
}
/**
 * @internal please do not use.
 */
function getLocationId(source) {
    if (source == null || typeof source !== 'object') {
        return '';
    }
    source = source.constructor.name;
    const errorTrace = stackTrace();
    const locationMatch = errorTrace.match(new RegExp(
    // get two lines beyond ActiveJS scope https://regexr.com/5eb9g
    `new ${source}\\b.+\\n(?:[\\s\\S]+new (?:AsyncSystem|Cluster)\\b.+\\n)?((?:.+\\n?){1,2})`));
    return hashCode((locationMatch === null || locationMatch === void 0 ? void 0 : locationMatch[1]) || errorTrace);
}

/**
 * @internal please do not use.
 */
const UniqueIdsAndLocationIdMap = {};
/**
 * @internal please do not use.
 */
const FrozenObj = Object.freeze({});
/**
 * The Global configuration for all ActiveJS constructs, Units, Systems, Action and Cluster.
 *
 * See {@link https://docs.activejs.dev/guides/configuration} for more details.
 *
 * @category 4. Utility
 */
class Configuration {
    // tslint:enable:variable-name
    /**
     * The default Storage API being used for storing the values of persistent Units.
     *
     * @default `localStorage`
     */
    static get storage() {
        return Configuration._storage || localStorage;
    }
    /**
     * Global ActiveJS environment configurations options.
     */
    static get ENVIRONMENT() {
        if (this.isDevMode()) {
            return Configuration._ENVIRONMENT;
        }
        return Object.freeze({});
    }
    /**
     * Configuration options applied to all the Actions. {@link Action}
     */
    static get ACTION() {
        return Configuration._ACTION;
    }
    /**
     * Configuration options applied to all the Clusters. {@link Cluster}
     */
    static get CLUSTER() {
        return Configuration._CLUSTER;
    }
    /**
     * Configuration options applied to all the Units. {@link Unit}
     */
    static get UNITS() {
        return Configuration._UNITS;
    }
    /**
     * Configuration options applied to all the BoolUnits. {@link BoolUnit}
     */
    static get BOOL_UNIT() {
        return Configuration._BOOL_UNIT;
    }
    /**
     * Configuration options applied to all the NumUnits. {@link NumUnit}
     */
    static get NUM_UNIT() {
        return Configuration._NUM_UNIT;
    }
    /**
     * Configuration options applied to all the StringUnits. {@link StringUnit}
     */
    static get STRING_UNIT() {
        return Configuration._STRING_UNIT;
    }
    /**
     * Configuration options applied to all the ListUnits. {@link ListUnit}
     */
    static get LIST_UNIT() {
        return Configuration._LIST_UNIT;
    }
    /**
     * Configuration options applied to all the DictUnits. {@link DictUnit}
     */
    static get DICT_UNIT() {
        return Configuration._DICT_UNIT;
    }
    /**
     * Configuration options applied to all the GenericUnits. {@link GenericUnit}
     */
    static get GENERIC_UNIT() {
        return Configuration._GENERIC_UNIT;
    }
    /**
     * Configuration options applied to all the AsyncSystems. {@link AsyncSystem}
     */
    static get ASYNC_SYSTEM() {
        return Configuration._ASYNC_SYSTEM;
    }
    /**
     * Sets and overrides default configurations. \
     * These configurations can still be overridden at the time of instantiation.
     *
     * It should only be called once in the whole app.
     * Calling it second time doesn't merge the new configuration,
     * it simply rewrites it.
     *
     * However, the defaults are not affected by this behavior, \
     * they have to be overridden specifically every time.
     *
     * @param config The configuration options.
     */
    static set(config) {
        const { storage, ENVIRONMENT, ACTION, UNITS, CLUSTER, BOOL_UNIT, NUM_UNIT, STRING_UNIT, LIST_UNIT, DICT_UNIT, GENERIC_UNIT, ASYNC_SYSTEM, } = Object.assign({}, config);
        Configuration._storage = storage;
        Configuration._ENVIRONMENT = Object.freeze(Object.assign({}, ENVIRONMENT));
        Configuration._ACTION = Object.freeze(Object.assign({}, ACTION));
        Configuration._CLUSTER = Object.freeze(Object.assign({}, CLUSTER));
        Configuration._UNITS = Object.freeze(Object.assign({}, UNITS));
        Configuration._BOOL_UNIT = Object.freeze(Object.assign({}, BOOL_UNIT));
        Configuration._NUM_UNIT = Object.freeze(Object.assign({}, NUM_UNIT));
        Configuration._STRING_UNIT = Object.freeze(Object.assign({}, STRING_UNIT));
        Configuration._LIST_UNIT = Object.freeze(Object.assign({}, LIST_UNIT));
        Configuration._DICT_UNIT = Object.freeze(Object.assign({}, DICT_UNIT));
        Configuration._GENERIC_UNIT = Object.freeze(Object.assign({}, GENERIC_UNIT));
        Configuration._ASYNC_SYSTEM = Object.freeze(Object.assign(Object.assign({}, ASYNC_SYSTEM), { UNITS: Object.assign({}, ASYNC_SYSTEM === null || ASYNC_SYSTEM === void 0 ? void 0 : ASYNC_SYSTEM.UNITS), QUERY_UNIT: Object.assign({}, ASYNC_SYSTEM === null || ASYNC_SYSTEM === void 0 ? void 0 : ASYNC_SYSTEM.QUERY_UNIT), DATA_UNIT: Object.assign({}, ASYNC_SYSTEM === null || ASYNC_SYSTEM === void 0 ? void 0 : ASYNC_SYSTEM.DATA_UNIT), ERROR_UNIT: Object.assign({}, ASYNC_SYSTEM === null || ASYNC_SYSTEM === void 0 ? void 0 : ASYNC_SYSTEM.ERROR_UNIT), PENDING_UNIT: Object.assign({}, ASYNC_SYSTEM === null || ASYNC_SYSTEM === void 0 ? void 0 : ASYNC_SYSTEM.PENDING_UNIT) }));
        if (Configuration.ENVIRONMENT.checkUniqueId === true) {
            Object.keys(UniqueIdsAndLocationIdMap).forEach(k => {
                delete UniqueIdsAndLocationIdMap[k];
            });
        }
    }
    /**
     * Resets all global configurations to their default/empty state. \
     * It doesn't affect any currently existing instances, it's only applicable to the instances created after this.
     */
    static reset() {
        Configuration.set(null);
    }
    /**
     * It sets the {@link ENVIRONMENT} to it's default configuration, \
     * i.e: it turns off all the checks declared in {@link ENVIRONMENT} and \
     * also, disables the extra logs.
     */
    static enableProdMode() {
        this._isDevMode = false;
    }
    /**
     * To check whether the production mode has been enabled or not.
     */
    static isDevMode() {
        return this._isDevMode;
    }
}
// tslint:disable:variable-name
/**
 * @internal please do not use.
 */
Configuration._isDevMode = true;
/**
 * @internal please do not use.
 */
Configuration._ENVIRONMENT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._ACTION = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._CLUSTER = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._UNITS = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._BOOL_UNIT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._NUM_UNIT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._STRING_UNIT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._LIST_UNIT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._DICT_UNIT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._GENERIC_UNIT = FrozenObj;
/**
 * @internal please do not use.
 */
Configuration._ASYNC_SYSTEM = FrozenObj;

/**
 * This is the most basic building block of ActiveJS.
 *
 * Base serves as the base for all fundamental ActiveJS constructs: Units, Systems, Actions and Clusters.
 *
 * This is an internal construct, normally you'd never have to use this class directly.
 * However, if you're just reading the documentation, or want to learn more about how ActiveJS works,
 * or want to extend this class to build something on your own, please continue.
 *
 * It extends RxJS `Observable`.
 * Source of this Observable is either a BehaviorSubject or Subject depending on the {@link BaseConfig.replay} flag.
 * By default, it's a BehaviourSubject for the Units, Systems and Clusters; and a Subject for Actions.
 *
 * There's another non-replaying Observable named `future$` to observe the value, whose source is always a simple Subject,
 * This Subject also serves as the source for default extended Observable when {@link BaseConfig.replay} is `false`.
 *
 * In simple terms Base is an elaborate RxJS Subject like construct, with custom features like replay on demand.
 * Although that's just one aspect of Base.
 *
 * It also implements `Object.prototype` methods
 * to make working with the stored value a bit easier and efficient.
 *
 * Other than that, It also provides functionalities like: On-demand Observable custom-event, to listen to events like manual replay.
 *
 * @category 2. Abstract
 */
class Base extends Observable {
    // tslint:enable:variable-name
    constructor(config) {
        super();
        /**
         * @internal please do not use.
         */
        this.futureSubject = new Subject();
        /**
         * An Observable to observe future values,
         * unlike the default Observable it doesn't replay when subscribed to,
         * rather it waits for the next value.
         */
        this.future$ = this.futureSubject.asObservable();
        /**
         * @internal please do not use.
         */
        this._emitCount = 0;
        this.config = Object.assign({}, config);
        if (this.config.id !== undefined) {
            if (!isValidId(this.config.id)) {
                throw new TypeError(`Invalid id provided, expected a non-empty string, got ${String(this.config.id)}`);
            }
            if (Configuration.ENVIRONMENT.checkUniqueId === true) {
                const locationId = getLocationId(this);
                if (UniqueIdsAndLocationIdMap[this.config.id] != null &&
                    UniqueIdsAndLocationIdMap[this.config.id] !== locationId) {
                    throw new TypeError(`Duplicate id "${this.config.id}" detected by "checkUniqueId" check, consider assigning a unique id.`);
                }
                UniqueIdsAndLocationIdMap[this.config.id] = locationId;
            }
        }
        else if (this.config.persistent === true) {
            throw new TypeError(`An id is required for persistence to work.`);
        }
        if (this.config.replay === false) {
            this.sourceSubject = this.futureSubject;
            this.source = this.future$;
        }
        else {
            this.sourceSubject = new BehaviorSubject(undefined);
            this.source = this.sourceSubject.asObservable();
        }
        this.setupEvents();
        Object.freeze(this.config);
    }
    /**
     * A counter to keep track of how many times has a Unit, System, Action or Cluster emitted.
     * @returns Number of times a Unit, System, Action or Cluster has emitted.
     */
    get emitCount() {
        return this._emitCount;
    }
    /**
     * Current value.
     * Used internally, for operations that don't mutate the value.
     *
     * @internal please do not use.
     *
     * @category Access Value
     */
    rawValue() {
        return this.value();
    }
    /**
     * Creates a new Observable using the default Observable as source.
     * Use this to conceal other aspects of a Unit, System, Action or Cluster except the Observable part.
     *
     * @returns An Observable with the value of a Unit, System, Action or Cluster.
     *
     * @category Common
     */
    asObservable() {
        const observable = new Observable();
        observable.source = this.source;
        return observable;
    }
    /**
     * To manually re-emit the last emitted value again.
     *
     * @triggers {@link EventReplay}
     * @category Common
     */
    replay() {
        var _a;
        this.emit(this.emittedValue);
        if ((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) {
            this.eventsSubject.next(new EventReplay(this.emittedValue));
        }
    }
    /**
     * Converts the value to JSON string, using `JSON.stringify`.
     *
     * @category Common
     */
    toJsonString() {
        return JSON.stringify(this.rawValue());
    }
    /**
     * Returns the {@link rawValue},
     * JavaScript automatically invokes it when encountering an object where a primitive value is expected.
     *
     * @alias {@link rawValue}
     * This method is not intended to be used by developers, use {@link rawValue} instead.
     * It only exists to be used by JavaScript implicitly.
     *
     * @hidden
     * @category Customised Object.prototype
     */
    valueOf() {
        return this.rawValue();
    }
    /**
     * Returns the {@link rawValue}.
     * JavaScript automatically invokes it when `JSON.stringify` is invoked on an object.
     *
     * @alias {@link rawValue}
     * This method is not intended to be used by developers, use {@link rawValue} instead.
     * It only exists to be used by `JSON.stringify` implicitly.
     *
     * @hidden
     * @category Common
     */
    toJSON() {
        return this.rawValue();
    }
    /**
     * @internal please do not use.
     */
    emit(value = this.value()) {
        ++this._emitCount;
        this.emittedValue = value;
        if (this.sourceSubject !== this.futureSubject) {
            this.sourceSubject.next(value);
        }
        this.futureSubject.next(value);
    }
    /**
     * @internal please do not use.
     */
    setupEvents() {
        Object.defineProperty(this, 'events$', {
            get() {
                if (this._events) {
                    return this._events;
                }
                this.eventsSubject = new Subject();
                this._events = this.eventsSubject.asObservable();
                Object.defineProperty(this, 'eventsSubject', { enumerable: false });
                Object.defineProperty(this, '_events', { enumerable: false });
                return this._events;
            },
            enumerable: false,
        });
    }
}
/**
 * @internal please do not use.
 */
const MethodsNotToImplement = [
    ...Object.getOwnPropertyNames(Observable.prototype),
    ...Object.getOwnPropertyNames(Base.prototype),
];
Object.getOwnPropertyNames(Object.prototype).forEach(method => {
    if (!isFunction(Object.prototype[method]) || MethodsNotToImplement.includes(method)) {
        return;
    }
    Object.defineProperty(Base.prototype, method, {
        value(...args) {
            return this.rawValue()[method](...args);
        },
    });
});

/**
 * A wrapper on RxJS Subscription.
 *
 * Stream is a simple construct that adds the ability to re-subscribe to the provided Observable.
 * Given an Observable, it immediately gets subscribed, and the subscription instance is saved as Stream's property.
 * The Stream keeps the reference to the provided Observable, and uses it for re-subscription when asked.
 *
 * See {@link https://docs.activejs.dev/utilities/stream} for more details.
 *
 * @category 4. Utility
 */
class Stream {
    /**
     * Given an Observable, subscribes to it immediately, and saves the Subscription instance.
     *
     * @param observable The Observable to be used for subscription.
     */
    constructor(observable) {
        this.observable = observable;
        if (!(observable instanceof Observable)) {
            throw new TypeError('Expected an Observable, got ' + observable);
        }
        this.subscribe();
        makeNonEnumerable(this);
    }
    /**
     * The current Subscription instance.
     * It can be `undefined` if there's no active Subscription.
     */
    get subscription() {
        return this._subscription;
    }
    /**
     * Indicates whether the Stream is active or not. \
     * i.e.: Whether the provided Observable is subscribed or not.
     */
    get isSubscribed() {
        return !!this._subscription;
    }
    /**
     * Subscribe to the provided Observable, and save the Subscription instance.
     *
     * @returns Subscription instance returned by {@link Observable.subscribe}.
     *
     * @category Common
     */
    subscribe() {
        this._subscription = this.observable.subscribe({ error: () => this.unsubscribe() });
        return this.subscription;
    }
    /**
     * Unsubscribes from current subscription instance using {@link Subscription.unsubscribe}. \
     * It also deletes the Subscription instance.
     *
     * @category Custom Stream
     */
    unsubscribe() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            delete this._subscription;
        }
    }
    /**
     * Unsubscribes and then immediately subscribes again,
     * also, replaces the {@link subscription} instance with the new Subscription instance.
     *
     * @returns Subscription instance returned by subscribing to the provided Observable {@link Observable.subscribe}.
     *
     * @category Custom Stream
     */
    resubscribe() {
        this.unsubscribe();
        return this.subscribe();
    }
}

/**
 * @internal
 * The prefix string for ids of persistent Units, applied when saving/retrieving value to/from persistent storage,
 * This is intended to avoid conflicts with other items in the storage that do not belong to ActiveJS,
 * and to identify items in the storage that belong to ActiveJS for features like {@link clearPersistentStorage} to work as intended.
 */
const KeyPrefix = '_AJS_UNIT_';
/**
 * To clear persisted values of persistent Units from storage.
 *
 * Note: It does not clear the value of Units, only the persisted value is cleared.
 *
 * See {@link https://docs.activejs.dev/guides/persistence} for more details.
 *
 * @param storage The Storage from where the Units' persisted values need to be removed. \
 * {@link Configuration.storage} is used as storage by default. \
 * You can pass a reference to whichever storage you want to clean up.
 * @category Global
 */
function clearPersistentStorage(storage = Configuration.storage) {
    Object.keys(storage).forEach(key => {
        if (key.startsWith(KeyPrefix)) {
            storage.removeItem(key);
        }
    });
}
/**
 * @internal please do not use.
 */
function save(key, value, storage = Configuration.storage) {
    let jsonString;
    try {
        // wrap the value to later easily determine whether any value has been
        // saved to storage or not.
        // eg: If storage.get('item') is null, it can mean many things,
        // but {value: null} can only mean that the value is null.
        jsonString = JSON.stringify({ value });
    }
    catch (e) {
        /* istanbul ignore next */
        jsonString = JSON.stringify({ value: String(value) });
    }
    storage.setItem(KeyPrefix + key, jsonString);
}
/**
 * @internal please do not use.
 */
function retrieve(key, storage = Configuration.storage) {
    const raw = storage.getItem(KeyPrefix + key);
    try {
        return JSON.parse(raw);
    }
    catch (e) {
        /* istanbul ignore next */
        return null;
    }
}
/**
 * @internal please do not use.
 */
function remove(key, storage = Configuration.storage) {
    storage.removeItem(KeyPrefix + key);
}

/**
 * @internal please do not use.
 */
function logInfo(...messages) {
    const { logLevel } = Configuration.ENVIRONMENT;
    if (isNumber(logLevel) && logLevel >= 2 /* INFO */) {
        // tslint:disable-next-line:no-console
        return console.info.bind(console, ...messages);
    }
    return NOOP;
}
/**
 * @internal please do not use.
 */
function logWarn(...messages) {
    const { logLevel } = Configuration.ENVIRONMENT;
    if (isNumber(logLevel) && logLevel >= 1 /* WARN */) {
        // tslint:disable-next-line:no-console
        return console.warn.bind(console, ...messages);
    }
    return NOOP;
}

function checkAsyncSystemConfig(config) {
    if (config) {
        if (config.clearDataOnError === true && config.clearDataOnQuery === true) {
            return logWarn(`When "clearDataOnQuery" is set to true,
"clearDataOnError" stops working, as only one of them can work at a time\n
Consider only setting one at a time.`);
        }
        if (config.clearErrorOnData === true && config.clearErrorOnQuery === true) {
            return logWarn(`When "clearErrorOnQuery" is set to true,
"clearErrorOnData" stops working, as only one of them can work at a time\n
Consider only setting one at a time.`);
        }
    }
    return NOOP;
}
function checkSerializability(o) {
    const [serializable, nonSerializableVal] = isSerializable(o);
    if (serializable === false) {
        throw new TypeError(`Non-serializable value ${String(nonSerializableVal)} of type ${nonSerializableVal.constructor.name}:${typeof nonSerializableVal} detected by "checkSerializability" check. Consider a serializable alternative.`);
    }
}
function checkClusterItems(items) {
    if (!isDict(items) || !Object.values(items).some(item => item instanceof Base)) {
        throw new TypeError(`No ActiveJS construct provided; expected at least one Unit, System, Action or Cluster; got ${String(items)}`);
    }
}
function checkPath(path) {
    if (!path.length) {
        throw new TypeError(`Expected at least one key`);
    }
    const invalidKeyIndex = path.findIndex(key => typeof key !== 'string' && typeof key !== 'number');
    if (invalidKeyIndex > -1) {
        const invalidKey = path[invalidKeyIndex];
        throw new TypeError(`Expected numbers and strings, but got ${invalidKey} of type ${typeof invalidKey}`);
    }
}

/**
 * UnitBase serves as the base for all the ActiveJS Units: GenericUnit, BoolUnit, ListUnit, etc.
 * It extends {@link Base}.
 *
 * This is an internal construct, normally you'd never have to use this class directly.
 * However, if you're just reading the documentation, or want to learn more about how ActiveJS works,
 * or want to extend this class to build something on your own, please continue.
 *
 * UnitBase creates the foundation of all the ActiveJS Units.
 * It implements the features like:
 * - dispatching, clearing and resetting the value
 * - caching the dispatched values
 * - navigating through the cached values, by methods like goBack, goForward, jump etc.
 * - observable events to listen to including but not limited to the above mentioned actions
 * - freezing/unfreezing the Unit
 * - muting/unmuting the Unit
 * - persisting and retrieving the value to/from persistent-storage
 * - debouncing the dispatch
 * - resetting the Unit
 * - etc.
 *
 * @category 2. Abstract
 */
class UnitBase extends Base {
    // tslint:enable:variable-name
    /**
     * @internal please do not use.
     */
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.UNITS), config));
        /**
         * @internal please do not use.
         */
        this._isFrozen = false;
        /**
         * @internal please do not use.
         */
        this._isMuted = false;
        /**
         * @internal please do not use.
         */
        this._cachedValues = [];
        /**
         * @internal please do not use.
         */
        this._cacheIndex = 0;
        /**
         * @internal please do not use.
         */
        this._initialValue = undefined;
        const { cacheSize, initialValue, dispatchDebounce, dispatchDebounceMode, persistent, } = this.config;
        this.cacheSize = isNumber(cacheSize) ? Math.max(1, cacheSize) : 2; // min 1, default 2
        if (persistent === true) {
            this.restoreValueFromPersistentStorage(initialValue);
        }
        else {
            this.checkSerializabilityMaybe(initialValue);
            this.dispatchInitialValue(this.deepCopyMaybe(initialValue));
        }
        if (dispatchDebounce === true || isNumber(dispatchDebounce)) {
            this.dispatchMiddleware = debounce(this.dispatchMiddleware.bind(this), dispatchDebounce, dispatchDebounceMode);
        }
    }
    /**
     * Indicates whether the Unit is frozen or not.
     * See {@link freeze} for more details.
     *
     * Note: It's not the same as [Object.isFrozen](https://cutt.ly/WyFdzPD).
     */
    get isFrozen() {
        return this._isFrozen;
    }
    /**
     * Indicates whether the Unit is muted or not.
     * See {@link mute} for more details.
     */
    get isMuted() {
        return this._isMuted;
    }
    /**
     * Indicates whether the value is undefined or not.
     *
     * It should be preferred if the Unit is configured to be immutable, as it doesn't create a copy.
     */
    get isEmpty() {
        return this.rawValue() === this.defaultValue();
    }
    /**
     * Count of all the cached values.
     */
    get cachedValuesCount() {
        return this._cachedValues.length;
    }
    /**
     * Index of the current {@link value} in the {@link UnitBase.cachedValues}
     */
    get cacheIndex() {
        return this._cacheIndex;
    }
    /**
     * The initialValue provided on instantiation.
     * Creates a copy if the Unit is configured to be immutable.
     *
     * @category Access Value
     */
    initialValue() {
        return this.deepCopyMaybe(this.initialValueRaw());
    }
    /**
     * Current value of the Unit.
     * Creates a copy if the Unit is configured to be immutable.
     *
     * @default
     * BoolUnit: `false` \
     * NumUnit: `0` \
     * StringUnit: `''` \
     * ListUnit: `[]` \
     * DictUnit: `{}` \
     * GenericUnit: `undefined`
     *
     * @category Access Value
     */
    value() {
        return this.deepCopyMaybe(this.rawValue()); // apply the fallback and kill reference
    }
    /**
     * If the Unit has a non-primitive value,
     * use it to get access to the current {@link value}, without creating a deep-copy.
     *
     * This can come in handy if the Unit is configured to be immutable, and you want to perform a non-mutating action
     * without creating a deep-copy of the value.
     *
     * @category Access Value
     */
    rawValue() {
        return this.applyFallbackValue(this._value);
    }
    /**
     * All the cached values.
     * Creates a copy if the Unit is configured to be immutable.
     *
     * @category Access Value
     */
    cachedValues() {
        return this._cachedValues.map(value => this.deepCopyMaybe(value));
    }
    /**
     * @internal please do not use.
     */
    initialValueRaw() {
        return this.applyFallbackValue(this._initialValue);
    }
    /**
     * @internal please do not use.
     */
    defaultValue() {
        return undefined;
    }
    /**
     * A helper method that creates a stream by subscribing to the Observable returned by the param `observableProducer` callback.
     *
     * Ideally the callback function creates an Observable by applying `Observable.pipe`.
     *
     * Just know that you should catch the error in a sub-pipe (ie: do not let it propagate to the main-pipe), otherwise
     * as usual the stream will stop working, and will not react on any further emissions.
     *
     * @param observableProducer A callback function that should return an Observable.
     *
     * @category Common
     */
    createStream(observableProducer) {
        const observable = observableProducer(this);
        return new Stream(observable);
    }
    /**
     * Given a value, this function determines whether it should be dispatched or not. \
     * The dispatch is denied in following circumstances:
     * - If the Unit is frozen. {@link isFrozen}
     * - If {@link UnitConfig.distinctDispatchCheck} is set to `true`, and the new-value === current-value,
     * - If {@link UnitConfig.customDispatchCheck} returns a `falsy` value.
     *
     * If the Unit is not frozen, you can bypass other dispatch-checks by passing param `force = true`.
     *
     * This function is used internally, when a value is dispatched {@link dispatch}. \
     * Even initialValue {@link UnitConfig.initialValue} dispatch has to pass this check.
     *
     * You can also use it to check if the value will be dispatched or not before dispatching it.
     *
     * @param value The value to be dispatched.
     * @param force Whether dispatch-checks should be bypassed or not.
     * @returns A boolean indicating whether the param `value` would pass the dispatch-checks if dispatched.
     *
     * @category Common Units
     */
    wouldDispatch(value, force = false) {
        if (this.isFrozen) {
            return false;
        }
        if (force === true) {
            return true;
        }
        if (typeof this.config.customDispatchCheck === 'function' &&
            !this.config.customDispatchCheck(this.rawValue(), value)) {
            return false;
        }
        return this.distinctCheck(value);
    }
    /**
     * Method to dispatch new value by passing the value directly, or \
     * by passing a value-producer-function that produces the value using the current {@link value}.
     *
     * Given a value, it either gets dispatched if it's allowed by {@link wouldDispatch}, \
     * or it gets ignored if not allowed.
     *
     * If the Unit is not configured to be immutable, then \
     * the value-producer-function (param `valueOrProducer`) should not mutate the current {@link value}, \
     * which is provided as an argument to the function.
     *
     * If you mutate the value, then the cached-value might also get mutated, \
     * as the cached-value is saved by reference, which can result in unpredictable state.
     *
     * @param valueOrProducer A new-value, or a pure function that produces a new-value.
     * @param options Dispatch options.
     * @returns `true` if value got dispatched, otherwise `false`.
     * If {@link UnitConfig.dispatchDebounce} is enabled, then it'll return `undefined`.
     *
     * @triggers {@link EventUnitDispatch}, or {@link EventUnitDispatchFail}, depending on the success of dispatch.
     * @category Common Action/Units
     */
    dispatch(valueOrProducer, options) {
        if ((options === null || options === void 0 ? void 0 : options.bypassDebounce) === true) {
            return this.dispatchActual(valueOrProducer, options);
        }
        return this.dispatchMiddleware(valueOrProducer, options);
    }
    /**
     * To manually re-emit the last emitted value again. \
     * It doesn't work if the Unit is frozen {@link isFrozen} or muted {@link isMuted}.
     *
     * Note: Even if the Unit is immutable, it does not create a copy of the Unit's value,
     * it merely re-emits the last emitted value.
     *
     * @returns `true` if replayed successfully, otherwise `false`.
     *
     * @triggers {@link EventReplay}
     * @category Common
     */
    replay() {
        if (this.isFrozen || this.isMuted) {
            return false;
        }
        super.replay();
        return true;
    }
    /**
     * Go back in the cache and re-emit the previous value from the cache, \
     * without creating a new entry in the cache.
     *
     * It can be used as Undo.
     *
     * It doesn't work if the Unit is frozen {@link isFrozen}.
     * It only works if there's a previously dispatched value in the cache. \
     * ie: the {@link cacheIndex} is not 0
     *
     * @returns `true` if the cache-navigation was successful, otherwise `false`.
     *
     * @triggers {@link EventUnitJump}
     * @category Cache Navigation
     */
    goBack() {
        return this.jump(-1);
    }
    /**
     * After going back in the cache (ie: re-emitting an old value from the cache), \
     * use this method to go to the next value, without creating a new entry in the cache.
     *
     * It can be used as Redo.
     *
     * It doesn't work if the Unit is frozen {@link isFrozen}.
     * It only works if the current {@link value} is not the last value in the cache. \
     * ie: the {@link cacheIndex} is not equal to `cachedValuesCount - 1`
     *
     * @returns `true` if the cache-navigation was successful, otherwise `false`
     *
     * @triggers {@link EventUnitJump}
     * @category Cache Navigation
     */
    goForward() {
        return this.jump(1);
    }
    /**
     * Use this method to re-emit the first value in the cache, \
     * without creating a new entry in the cache.
     *
     * It doesn't work if the Unit is frozen {@link isFrozen}.
     * It only works if the {@link cacheIndex} is not already at the last value in the cache. \
     * ie: the {@link cacheIndex} is not 0.
     *
     * @returns `true` if the cache-navigation was successful, otherwise `false`
     *
     * @triggers {@link EventUnitJump}
     * @category Cache Navigation
     */
    jumpToStart() {
        return this.jump(-this.cacheIndex);
    }
    /**
     * After going back in the cache (ie: re-emitting an old value from the cache), \
     * use this method to re-dispatch the last (latest) value in the cache, \
     * without creating a new entry in the cache.
     *
     * It doesn't work if the Unit is frozen {@link isFrozen}.
     * It only works if the {@link cacheIndex} is not already at the last value in the cache.
     *
     * @returns `true` if the cache-navigation was successful, otherwise `false`
     *
     * @triggers {@link EventUnitJump}
     * @category Cache Navigation
     */
    jumpToEnd() {
        return this.jump(this.cachedValuesCount - 1 - this.cacheIndex);
    }
    /**
     * Use this method to re-emit a value from the cache, by jumping specific steps backwards or forwards, \
     * without creating a new entry in the cache.
     *
     * It doesn't work if the Unit is frozen {@link isFrozen} or `steps` is not a `number`.
     * It only works if the new calculated index is in the bounds of {@link cachedValues}, \
     * ie: the new-index is >= 0, and less than {@link cachedValuesCount}, but \
     * not equal to current {@link cacheIndex}.
     *
     * @param steps Number of steps to jump in the cache, negative to jump backwards, positive to jump forwards
     * @returns `true` if the cache-navigation was successful, otherwise `false`.
     *
     * @triggers {@link EventUnitJump}
     * @category Cache Navigation
     */
    jump(steps) {
        var _a;
        if (this.isFrozen || !isNumber(steps)) {
            return false;
        }
        const newIndex = this.cacheIndex + steps;
        // no point going forward
        if (newIndex < 0 || newIndex === this.cacheIndex || newIndex > this.cachedValuesCount - 1) {
            return false;
        }
        this._cacheIndex = newIndex;
        this.updateValueAndCache(this._cachedValues[this.cacheIndex], null, true);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitJump(steps, newIndex));
        }
        return true;
    }
    /**
     * Get cached value at a given index.
     *
     * @param index The index of cached value
     * @returns The cached value if it exists, otherwise undefined
     *
     * @category Common Units
     */
    getCachedValue(index) {
        return this._cachedValues.hasOwnProperty(index)
            ? this.deepCopyMaybe(this._cachedValues[index])
            : undefined;
    }
    /**
     * Clears the cached values, current {@link value} stays intact, but it gets removed from the cache. \
     * Meaning, if you dispatch a new value you can't {@link goBack}. \
     * To keep the last value in the cache, pass `{leaveLast: true}` in the param `options`.
     *
     * It only works if the Unit is not frozen and there's something left to clear after evaluating the param `options`.
     *
     * Similar to preserving the last value, you can preserve the first value by passing `{leaveFirst: true}`.
     * Or preserve both first and last value by passing both options together.
     *
     * @param options Clear cache options
     * @returns `true` if the cache was cleared, otherwise `false`
     *
     * @triggers {@link EventUnitClearCache}
     * @category Common Units
     */
    clearCache(options) {
        var _a;
        const leaveFirst = (options === null || options === void 0 ? void 0 : options.leaveFirst) === true;
        const leaveLast = (options === null || options === void 0 ? void 0 : options.leaveLast) === true;
        if (this.isFrozen ||
            this.cachedValuesCount === 0 ||
            (this.cachedValuesCount === 1 && (leaveFirst || leaveLast)) ||
            (this.cachedValuesCount === 2 && leaveFirst && leaveLast)) {
            return false;
        }
        const start = leaveFirst ? 1 : 0;
        const deleteCount = this.cachedValuesCount - start - (leaveLast ? 1 : 0);
        this._cachedValues.splice(start, deleteCount);
        this._cacheIndex = Math.max(0, this.cachedValuesCount - 1);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitClearCache(options));
        }
        return true;
    }
    /**
     * Clears the value by dispatching the default value. \
     * It only works if the Unit is not frozen, and {@link emitCount} is not 0, and value is not empty {@link isEmpty}.
     *
     * @returns `true` if the value was cleared, otherwise `false`
     *
     * @triggers {@link EventUnitClearValue}
     * @category Common Units
     */
    clearValue() {
        var _a;
        if (this.isFrozen || this.emitCount === 0 || this.isEmpty) {
            return false;
        }
        this.updateValueAndCache(this.defaultValue());
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitClearValue());
        }
        return true;
    }
    /**
     * Holistically clears the Unit, \
     * unfreezes using {@link unfreeze}, \
     * clears the value using {@link clearValue}, \
     * completely clears cache using {@link clearCache}, \
     * in that specific order.
     *
     * @param options Clear cache options for {@link clearCache}.
     *
     * @triggers {@link EventUnitClear}
     * @category Common Units
     */
    clear(options) {
        var _a;
        this.unfreeze();
        this.clearValue();
        this.clearCache(options);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitClear(options));
        }
    }
    /**
     * Resets the value by dispatching the initial-value, {@link UnitConfig.initialValue} if provided, \
     * otherwise dispatches the default value.
     *
     * It only works if the Unit is not frozen, \
     * and the {@link value} is not equal to the {@link initialValue}.
     *
     * @returns `true` if the reset was successful, otherwise `false`
     *
     * @triggers {@link EventUnitResetValue}
     * @category Common Units
     */
    resetValue() {
        var _a;
        if (this.isFrozen || this.rawValue() === this.initialValueRaw()) {
            return false;
        }
        this.updateValueAndCache(this.initialValueRaw());
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitResetValue());
        }
        return true;
    }
    /**
     * Holistically resets the Unit, \
     * unfreezes using {@link unfreeze}, \
     * resets the value using {@link resetValue}, \
     * clears cache using {@link clearCache} and by default leaves last value; \
     * in that specific order.
     *
     * @param options Clear cache options for {@link clearCache}. default is `{leaveLast: true}`
     *
     * @triggers {@link EventUnitReset}
     * @category Common Units
     */
    reset(options = { leaveLast: true }) {
        var _a;
        this.unfreeze();
        this.resetValue();
        this.clearCache(options);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitReset(options));
        }
    }
    /**
     * Temporarily disables most of the functions of the Unit, except {@link unfreeze}, \
     * {@link mute}/{@link unmute}, {@link clear} and {@link reset}.
     *
     * It's not the same as `Object.freeze`.
     *
     * Freezing prevents any new values from getting dispatched, \
     * it disables all the mutating functions. \
     * Which eventually ensures that no event is emitted while the Unit is frozen, \
     * however all the read operations and operations that do not emit a value are allowed.
     *
     * @triggers {@link EventUnitFreeze}
     * @category Common Units
     */
    freeze() {
        var _a;
        // tslint:disable-next-line:no-console
        console.trace();
        if (this.isFrozen) {
            return;
        }
        this._isFrozen = true;
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitFreeze());
        }
    }
    /**
     * Unfreezes the Unit, and re-enables all the functions disabled by {@link freeze}. \
     * It only works if the Unit is frozen.
     *
     * @triggers {@link EventUnitUnfreeze}
     * @category Common Units
     */
    unfreeze() {
        var _a;
        if (!this.isFrozen) {
            return;
        }
        this._isFrozen = false;
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventUnitUnfreeze());
        }
    }
    /**
     * Mute the Unit, to stop emitting values as well as events, so that the subscribers are not triggered. \
     * All other functionalities stay unaffected. ie: cache it still updated, value is still updated.
     *
     * Note: If you subscribe to the default Observable while the Unit is muted, \
     * it will replay the last value emitted before muting the Unit, \
     * because new values are not being emitted.
     *
     * @category Common Units
     */
    mute() {
        this._isMuted = true;
    }
    /**
     * Unmute the Unit, to resume emitting values, and events. \
     * If a value was dispatched while the Unit was muted, the most recent value immediately gets emitted, \
     * so that subscribers can be in sync again. \
     * However, other {@link events$} are lost, and they will only emit on the next event.
     *
     * It only works if the Unit is muted. \
     * Moreover, it works even if the Unit is frozen, \
     * but no value will be emitted because no values would have been dispatched while the Unit was frozen.
     *
     * @triggers {@link EventUnitUnmute}
     * @category Common Units
     */
    unmute() {
        var _a;
        if (!this.isMuted) {
            return;
        }
        this._isMuted = false;
        if (this.emitOnUnmute) {
            this.emit();
            this.emitOnUnmute = null;
        }
        if ((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) {
            this.eventsSubject.next(new EventUnitUnmute());
        }
    }
    /**
     * Clears persisted value from persistent storage. \
     * It doesn't turn off persistence, future values will get persisted again.
     *
     * It only works if the Unit is configured to be persistent. ie: `options.persistent` is true.
     *
     * @returns `true` if the Unit is configured to be persistent, otherwise `false`.
     *
     * @triggers {@link EventUnitClearPersistedValue}
     * @category Common Units
     */
    clearPersistedValue() {
        var _a;
        if (this.config.persistent === true) {
            remove(this.config.id, this.config.storage);
            if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
                this.eventsSubject.next(new EventUnitClearPersistedValue());
            }
            return true;
        }
        return false;
    }
    /**
     * @internal please do not use.
     */
    deepCopyMaybe(o) {
        return this.config.immutable === true ? deepCopy(o) : o;
    }
    /**
     * @internal please do not use.
     */
    checkSerializabilityMaybe(o) {
        if (Configuration.ENVIRONMENT.checkSerializability === true) {
            checkSerializability(o);
        }
    }
    /**
     * @internal please do not use.
     */
    updateValueAndCache(value, options, skipCache = false) {
        const { cacheReplace } = options || {};
        if (Configuration.ENVIRONMENT.checkImmutability === true) {
            deepFreeze(value);
        }
        if (!skipCache) {
            this.updateCache(value, cacheReplace);
        }
        this._value = value;
        if (this.isMuted) {
            this.emitOnUnmute = true;
        }
        else {
            this.emitOnUnmute = null;
            this.emit();
        }
        this.updateValueInPersistentStorage();
    }
    /**
     * @internal please do not use.
     */
    applyFallbackValue(value) {
        return value === undefined ? this.defaultValue() : value;
    }
    /**
     * @internal please do not use.
     */
    distinctCheck(value) {
        return this.config.distinctDispatchCheck !== true || value !== this.rawValue();
    }
    /**
     * @internal please do not use.
     */
    dispatchMiddleware(valueOrProducer, options) {
        return this.dispatchActual(valueOrProducer, options);
    }
    /**
     * @internal please do not use.
     */
    dispatchActual(valueOrProducer, options) {
        var _a, _b;
        const { force } = options || {};
        const value = typeof valueOrProducer === 'function'
            ? valueOrProducer(this.value())
            : valueOrProducer;
        this.checkSerializabilityMaybe(value);
        if (this.wouldDispatch(value, force)) {
            this.updateValueAndCache(this.deepCopyMaybe(value), options); // clone and dispatch
            if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
                this.eventsSubject.next(new EventUnitDispatch(value, options));
            }
            return true;
        }
        if (((_b = this.eventsSubject) === null || _b === void 0 ? void 0 : _b.observers.length) && !this.isMuted) {
            const failReason = (this.isFrozen && DispatchFailReason.FROZEN_UNIT) ||
                (this.isValidValue(value) &&
                    (this.distinctCheck(value)
                        ? DispatchFailReason.CUSTOM_DISPATCH_CHECK
                        : DispatchFailReason.DISTINCT_CHECK)) ||
                DispatchFailReason.INVALID_VALUE;
            this.eventsSubject.next(new EventUnitDispatchFail(value, failReason, options));
        }
        return false;
    }
    /**
     * @internal please do not use.
     */
    shouldDispatchInitialValue(initialValue) {
        // not checking undefined in wouldDispatch to allow undefined values later on (for GenericUnit)
        return initialValue !== undefined && this.isValidValue(initialValue);
    }
    /**
     * @internal please do not use.
     */
    updateCache(value, cacheReplace) {
        if (cacheReplace === true) {
            this._cachedValues[this.cacheIndex] = value;
        }
        else {
            if (this.cachedValuesCount === 0 || this.cacheIndex === this.cachedValuesCount - 1) {
                this._cachedValues.push(value);
                if (this.cachedValuesCount > this.cacheSize) {
                    this._cachedValues.shift();
                }
                this._cacheIndex = this.cachedValuesCount - 1;
            }
            else {
                this._cachedValues.splice(this.cacheIndex + 1, this.cachedValuesCount - 1 - this.cacheIndex, value);
                ++this._cacheIndex;
            }
        }
    }
    /**
     * @internal please do not use.
     */
    dispatchInitialValue(initialValue) {
        if (this.shouldDispatchInitialValue(initialValue)) {
            this._initialValue = initialValue;
            this.updateValueAndCache(this.initialValueRaw());
        }
        else {
            this.updateValueAndCache(this.defaultValue());
        }
    }
    /**
     * @internal please do not use.
     */
    updateValueInPersistentStorage() {
        if (this.config.persistent === true) {
            save(this.config.id, this.rawValue(), this.config.storage);
        }
    }
    /**
     * @internal please do not use.
     */
    restoreValueFromPersistentStorage(initialValue) {
        const savedState = retrieve(this.config.id, this.config.storage);
        if (savedState) {
            this.dispatchInitialValue(savedState.value);
        }
        else {
            this.checkSerializabilityMaybe(initialValue);
            this.dispatchInitialValue(this.deepCopyMaybe(initialValue));
        }
    }
}

/**
 * Selection offers the ability to operate on nested properties in a Unit's value.
 *
 * Selection is a simple construct that operates on a nested property in
 * the non-primitive Units' value, ie: ListUnit, GenericUnit and DictUnit.
 *
 * See {@link https://docs.activejs.dev/utilities/selection} for more details.
 *
 * @category 4. Utility
 */
class Selection {
    constructor(unit, path) {
        this.unit = unit;
        this.path = path;
        checkPath(path);
    }
    value() {
        const valAtPath = plucker(this.unit.rawValue(), this.path);
        return this.unit.deepCopyMaybe(valAtPath);
    }
    asObservable() {
        if (this.unit.config.immutable === true) {
            // allows to optimize cases when the whole value is not new,
            // e.g.: updating property 'a' in a DictUnit using 'set',
            // wouldn't trigger paths that start with property 'b'
            return this.unit.pipe(map(() => plucker(this.unit.rawValue(), this.path)), distinctUntilChanged(), map(() => plucker(this.unit.emittedValue, this.path)));
        }
        return this.unit.pipe(map(value => plucker(value, this.path)), distinctUntilChanged());
    }
}

/**
 * Base for non-primitive Units.
 *
 * - NonPrimitiveUnitBase extends {@link UnitBase}
 * - Which further extends {@link Base} and `Observable`
 *
 * @category 2. Abstract
 */
class NonPrimitiveUnitBase extends UnitBase {
    constructor(config) {
        super(config);
        makeNonEnumerable(this);
    }
    /**
     * Returns the names of the properties of the current {@link value} using `Object.keys`. \
     *
     * In case of a GenericUnit,
     * if current {@link value} is null or undefined, it returns an empty `array`, \
     * where `Object.keys` would have failed.
     *
     * @category Common List/Dict/Generic Units
     */
    objectKeys() {
        return this.rawValue() == null ? [] : Object.keys(this.rawValue());
    }
    /**
     * Returns an `array` of key/values of the properties of the current {@link value} using `Object.entries`. \
     *
     * In case of a GenericUnit,
     * if current {@link value} is null or undefined, it returns an empty `array`, \
     * where `Object.entries` would have failed.
     *
     * @category Common List/Dict/Generic Units
     */
    objectEntries() {
        return this.rawValue() == null ? [] : Object.entries(this.value());
    }
    /**
     * Returns an `array` of values of the properties of the current {@link value} using `Object.values`. \
     *
     * In case of a GenericUnit,
     * if current {@link value} is null or undefined, it returns an empty `array`, \
     * where `Object.values` would have failed.
     *
     * @category Common List/Dict/Generic Units
     */
    objectValues() {
        return this.rawValue() == null ? [] : Object.values(this.value());
    }
    // tslint:enable:max-line-length
    /**
     * Select a nested property at a certain path in the Unit's {@link value}.
     * It doesn't fail even if a value in middle of the path is undefined.
     *
     * It can be used to create an Observable to listen to changes in a nested property
     * using {@link Selection.asObservable} method.
     *
     * It provides static value access through the {@link Selection.value} method.
     * It can be helpful when the Unit is configured to be immutable,
     * as you only clone the nested property instead of the whole value.
     *
     * @example
     * ```typescript
     * // create a Unit
     * const unit = new DictUnit();
     *
     * // create a selection
     * const selection = unit.select('a', 'b', 0);
     * // create an Observable
     * const selection$ = selection.asObservable();
     * // subscribe to the selector
     * selection.subscribe(value => console.log(value)) // logs undefined immediately
     *
     * // dispatch a value
     * unit.dispatch({a: {b: ['hi', 'there']}});
     * // 'hi' will get logged to the console
     *
     * // update a value on a different path
     * unit.set('c', 'other');
     * // the selector won't be triggered by this
     * ```
     *
     * @param path Property keys and indexes of the path you want to select.
     * @returns A {@link Selection} object of the selected property or path.
     *
     * @category Common List/Dict/Generic Units
     */
    select(...path) {
        return new Selection(this, path);
    }
}

/**
 * GenericUnit is a reactive storage Unit that doesn't pertain to any specific data type.
 *
 * GenericUnit accepts all data types as its value.
 *
 * Learn more about Units [here](https://docs.activejs.dev/fundamentals/units). \
 * Learn more about GenericUnit [here](https://docs.activejs.dev/fundamentals/units/genericunit).
 *
 * Just like every other Non-Primitive ActiveJS Unit:
 * - GenericUnit extends {@link NonPrimitiveUnitBase}
 * - Which further extends {@link UnitBase}, {@link Base} and `Observable`
 *
 * @category 1. Units
 */
class GenericUnit extends NonPrimitiveUnitBase {
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.GENERIC_UNIT), config));
        makeNonEnumerable(this);
    }
    /**
     * The underlying method that makes `skipNilValues` possible for {@link goBack} and {@link goForward}.
     *
     * @param direction The direction to find the non `null` or `undefined` value in.
     *
     * @hidden
     * @category Cache Navigation
     */
    goToNonNilValue(direction) {
        if (this.isFrozen) {
            return false;
        }
        const lastNonNilValueIndex = direction === 'BACK'
            ? findIndexBackwards(this._cachedValues, v => v != null, this.cacheIndex - 1)
            : findIndex(this._cachedValues, v => v != null, this.cacheIndex + 1);
        if (lastNonNilValueIndex === -1) {
            return false;
        }
        return this.jump(lastNonNilValueIndex - this.cacheIndex);
    }
    /**
     * It extends {@link UnitBase.goBack}, and allows skipping over nil (null or undefined) values. \
     * To skip over nil values pass `true` as `skipNilValues`.
     *
     * @param skipNilValues Should the `null` and `undefined` values be skipped over.
     *
     * @category Cache Navigation
     */
    goBack(skipNilValues = false) {
        if (skipNilValues === true) {
            return this.goToNonNilValue('BACK');
        }
        return super.goBack();
    }
    /**
     * It extends {@link UnitBase.goForward}, and allows skipping over nil (null or undefined) values. \
     * To skip over nil values pass `true` as `skipNilValues`.
     *
     * @param skipNilValues Should the `null` and `undefined` values be skipped over.
     *
     * @category Cache Navigation
     */
    goForward(skipNilValues = false) {
        if (skipNilValues === true) {
            return this.goToNonNilValue('FORWARD');
        }
        return super.goForward();
    }
    /**
     * @internal please do not use.
     */
    isValidValue(value) {
        return true;
    }
}

/**
 * BoolUnit is a reactive storage Unit that stores `boolean` value.
 *
 * It only accepts `boolean` data type as its value.
 * It ensures that at any point of time the value would always be `boolean`.
 *
 * Learn more about Units [here](https://docs.activejs.dev/fundamentals/units). \
 * Learn more about BoolUnit [here](https://docs.activejs.dev/fundamentals/units/boolunit).
 *
 * Just like every other ActiveJS Unit:
 * - BoolUnit extends {@link UnitBase}
 * - Which further extends {@link Base} and `Observable`
 *
 * @category 1. Units
 */
class BoolUnit extends UnitBase {
    /**
     * Current value of the Unit.
     * @default `false` (boolean false)
     *
     * @category Access Value
     */
    value() {
        return this.rawValue();
    }
    /**
     * @internal please do not use.
     */
    defaultValue() {
        return false;
    }
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.BOOL_UNIT), config));
        makeNonEnumerable(this);
    }
    /**
     * Extends {@link UnitBase.wouldDispatch} and adds additional check for type boolean,
     * which cannot be bypassed even by using param `force`.
     *
     * @param value The value to be dispatched.
     * @param force Whether dispatch-checks should be bypassed or not.
     * @returns A boolean indicating whether the param `value` would pass the dispatch-checks if dispatched.
     *
     * @category Common Units
     */
    wouldDispatch(value, force) {
        return this.isValidValue(value) && super.wouldDispatch(value, force);
    }
    /**
     * @internal please do not use.
     */
    isValidValue(value) {
        return typeof value === 'boolean';
    }
}

/**
 * StringUnit is a reactive storage Unit that emulates `string`.
 *
 * It only accepts `string` data type as its value.
 * It ensures that at any point of time the value would always be `string`.
 *
 * StringUnit implements all the `String.prototype` methods that are available
 * in the environment/browser its running, including polyfills.
 * e.g.: `trim`, `match`, `includes`, etc.
 *
 * Learn more about Units [here](https://docs.activejs.dev/fundamentals/units). \
 * Learn more about StringUnit [here](https://docs.activejs.dev/fundamentals/units/stringunit).
 *
 * Just like every other ActiveJS Unit:
 * - StringUnit extends {@link UnitBase}
 * - Which further extends {@link Base} and `Observable`
 *
 * @category 1. Units
 */
class StringUnit extends UnitBase {
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.STRING_UNIT), config));
        makeNonEnumerable(this);
    }
    /**
     * Length of the string {@link value}.
     */
    get length() {
        return this.rawValue().length;
    }
    /**
     * Current value of the Unit.
     * @default `''` (empty string)
     *
     * @category Access Value
     */
    value() {
        return this.rawValue();
    }
    /**
     * @internal please do not use.
     */
    defaultValue() {
        return '';
    }
    /**
     * Extends {@link UnitBase.wouldDispatch} and adds additional check for type string,
     * which cannot be bypassed even by using {@link force}.
     *
     * @param value The value to be dispatched.
     * @param force Whether dispatch-checks should be bypassed or not.
     * @returns A boolean indicating whether the param `value` would pass the dispatch-checks if dispatched.
     *
     * @category Common Units
     */
    wouldDispatch(value, force) {
        return this.isValidValue(value) && super.wouldDispatch(value, force);
    }
    /**
     * @internal please do not use.
     */
    isValidValue(value) {
        return typeof value === 'string';
    }
    /**
     * @internal please do not use.
     */
    [IteratorSymbol]() {
        return String.prototype[IteratorSymbol].call(this.value());
    }
}
/**
 * @internal please do not use.
 */
const MethodsNotToImplement$1 = [
    ...Object.getOwnPropertyNames(Observable.prototype),
    ...Object.getOwnPropertyNames(Base.prototype),
    ...Object.getOwnPropertyNames(UnitBase.prototype),
    ...Object.getOwnPropertyNames(StringUnit.prototype),
];
Object.getOwnPropertyNames(String.prototype).forEach(method => {
    if (!isFunction(String.prototype[method]) || MethodsNotToImplement$1.includes(method)) {
        return;
    }
    Object.defineProperty(StringUnit.prototype, method, {
        value(...args) {
            return String.prototype[method].apply(this.value(), args);
        },
    });
});

/**
 * NumUnit is a reactive storage Unit that emulates `number`.
 *
 * It only accepts `number` data type as its value.
 * It ensures that at any point of time the value would always be `number`.
 *
 * NumUnit implements all the `Number.prototype` methods that are available
 * in the environment/browser its running, including polyfills.
 * e.g.: `toFixed`, `toPrecision`, etc.
 *
 * Learn more about Units [here](https://docs.activejs.dev/fundamentals/units). \
 * Learn more about NumUnit [here](https://docs.activejs.dev/fundamentals/units/numunit).
 *
 * Just like every other ActiveJS Unit:
 * - NumUnit extends {@link UnitBase}
 * - Which further extends {@link Base} and `Observable`
 *
 * @category 1. Units
 */
class NumUnit extends UnitBase {
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.NUM_UNIT), config));
        makeNonEnumerable(this);
    }
    /**
     * Current value of the Unit.
     * @default `0` (number zero)
     *
     * @category Access Value
     */
    value() {
        return this.rawValue();
    }
    /**
     * @internal please do not use.
     */
    defaultValue() {
        return 0;
    }
    /**
     * Extends {@link UnitBase.wouldDispatch} and adds additional check for type number,
     * which cannot be bypassed even by using param `force`.
     *
     * @param value The value to be dispatched.
     * @param force Whether dispatch-checks should be bypassed or not.
     * @returns A boolean indicating whether the param `value` would pass the dispatch-checks if dispatched.
     *
     * @category Common Units
     */
    wouldDispatch(value, force) {
        return this.isValidValue(value) && super.wouldDispatch(value, force);
    }
    /**
     * @internal please do not use.
     */
    isValidValue(value) {
        return isNumber(value);
    }
}
/**
 * @internal please do not use.
 */
const MethodsNotToImplement$2 = [
    ...Object.getOwnPropertyNames(Observable.prototype),
    ...Object.getOwnPropertyNames(Base.prototype),
    ...Object.getOwnPropertyNames(UnitBase.prototype),
    ...Object.getOwnPropertyNames(NumUnit.prototype),
];
Object.getOwnPropertyNames(Number.prototype).forEach(method => {
    if (!isFunction(Number.prototype[method]) || MethodsNotToImplement$2.includes(method)) {
        return;
    }
    Object.defineProperty(NumUnit.prototype, method, {
        value(...args) {
            return Number.prototype[method].apply(this.value(), args);
        },
    });
});

/**
 * ListUnit is a reactive storage Unit that emulates `array`.
 *
 * ListUnit only accepts `array` data type as its value.
 * It ensures that at any point of time the value would always be an `array`.
 *
 * ListUnit is based on `array`, it implements all the `Array.prototype` methods that are available
 * in the environment/browser its running, including polyfills.
 * e.g.: `find`, `filter`, `includes`, etc.
 *
 * Learn more about Units [here](https://docs.activejs.dev/fundamentals/units). \
 * Learn more about ListUnit [here](https://docs.activejs.dev/fundamentals/units/listunit).
 *
 * Just like every other Non-Primitive ActiveJS Unit:
 * - ListUnit extends {@link NonPrimitiveUnitBase}
 * - Which further extends {@link UnitBase}, {@link Base} and `Observable`
 *
 * @category 1. Units
 */
class ListUnit extends NonPrimitiveUnitBase {
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.LIST_UNIT), config));
        makeNonEnumerable(this);
    }
    /**
     * The length of the list-{@link value}.
     */
    get length() {
        return this.rawValue().length;
    }
    /**
     * Indicates whether the length of the list-{@link value} is `0` or not.
     */
    get isEmpty() {
        return this.length === 0;
    }
    /**
     * @internal please do not use.
     */
    defaultValue() {
        return [];
    }
    /**
     * Extends {@link UnitBase.wouldDispatch} and adds additional check for type `array` {@see {@link Array.isArray}}, \
     * which cannot be bypassed even by using {@link force}.
     *
     * @param value The value to be dispatched.
     * @param force Whether dispatch-checks should be bypassed or not.
     * @returns A boolean indicating whether the param `value` would pass the dispatch-checks if dispatched.
     *
     * @category Common Units
     */
    wouldDispatch(value, force) {
        return this.isValidValue(value) && super.wouldDispatch(value, force);
    }
    /**
     * Sets the item on the given index in the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the index is valid-numeric.
     *
     * @param index The index of the item. \
     * A negative index is normalized as following: \
     * - If the index is less than negative of list-{@link length} it's treated as 0.
     * - Otherwise, it's subtracted from the list-{@link length}.
     * eg: If list length is `5`, negative index `-5` will be normalized to `0`, \
     * negative index `-6` will be normalized to `0`, and so on.
     * @param item The item.
     *
     * @triggers {@link EventListUnitSet}
     * @category Custom ListUnit
     */
    set(index, item) {
        var _a;
        if (this.isFrozen || !isValidIndex(index)) {
            return;
        }
        this.checkSerializabilityMaybe(item);
        const listShallowCopy = [...this.rawValue()];
        listShallowCopy[normalizeIndex(index, this.length)] = this.deepCopyMaybe(item);
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitSet(index, item));
        }
    }
    /**
     * Inserts items to the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen and there's at least 1 item to be inserted.
     *
     * @param start The zero-based index in the list from which to start adding items. \
     * A negative index is normalized as following: \
     * - If the index is less than negative of list-{@link length} it's treated as 0.
     * - Otherwise, it's subtracted from the list-{@link length}.
     * eg: If list length is `5`, negative index `-5` will be normalized to `0`, \
     * negative index `-6` will be normalized to `0`, and so on.
     * @param items The items to be insert.
     * @returns The new length of the list.
     *
     * @triggers {@link EventListUnitSplice}
     * @category Custom ListUnit
     */
    insert(start, ...items) {
        if (this.isFrozen || !items.length) {
            return this.length;
        }
        this.checkSerializabilityMaybe(items);
        this.splice(start, 0, ...items); // splice also takes care of empty spaces
        return this.length;
    }
    /**
     * Removes items at given indices from the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value. \
     * It modifies the length of the list as these indices are not deleted,
     * rather removed using `Array.prototype.splice`.
     *
     * It only works if the Unit is not frozen, and
     * the list is not empty, and at least 1 valid-numeric index is provided.
     *
     * Notes:
     * - The indices are removed in descending order.
     * - The indices are deduplicated.
     * - Negative indices are normalized before starting the removal. \
     * A negative index is normalized as following: \
     * - If the index is less than negative of list-{@link length}, it's treated as 0.
     * - Otherwise, it's subtracted from the list-{@link length}.
     * eg: If list length is `5`, negative index `-5` will be normalized to `0`, \
     * negative index `-6` will be normalized to `0`, and so on.
     *
     * @param indices The indices of the items to be removed.
     * @returns The removed items or an empty `array`.
     *
     * @triggers {@link EventListUnitRemove}
     * @category Custom ListUnit
     */
    remove(...indices) {
        var _a;
        const listLength = this.length;
        indices = sanitizeIndices(indices, listLength).filter(i => this.hasOwnProperty(i));
        if (this.isFrozen || !indices.length || this.isEmpty) {
            return [];
        }
        indices.sort().reverse();
        const removedItems = [];
        const listShallowCopy = [...this.rawValue()];
        indices.forEach(index => {
            removedItems.push(...this.deepCopyMaybe(listShallowCopy.splice(index, 1)));
        });
        this.updateValueAndCache(listShallowCopy);
        removedItems.reverse();
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitRemove(indices, removedItems));
        }
        return removedItems;
    }
    /**
     * Removes items from the list-{@link value} where the predicate returns `truthy` value. \
     * Dispatches a new copy of the value, without mutating the current-value. \
     * It modifies the length of the list as these indices are not deleted,
     * rather removed using `Array.prototype.splice`.
     *
     * It only works if the Unit is not frozen, and
     * the predicate is a function, and the list is not empty. \
     * It uses {@link remove} internally, for actual removal.
     *
     * @param predicate A callback function that gets called for every item in the list with the item and index as arguments.
     * @returns The removed items or an empty `array`.
     *
     * @triggers {@link EventListUnitRemove}
     * @category Custom ListUnit
     */
    removeIf(predicate) {
        if (this.isFrozen || typeof predicate !== 'function' || this.isEmpty) {
            return [];
        }
        const indicesToBeRemoved = [];
        this.value().forEach((item, index) => {
            if (predicate(item, index)) {
                indicesToBeRemoved.push(index);
            }
        });
        return this.remove(...indicesToBeRemoved);
    }
    /**
     * Deletes items at given indices in the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value. \
     * It doesn't modify the length of the list as these indices are only deleted, not removed.
     *
     * It only works if the Unit is not frozen, and
     * the list is not empty, and at least 1 valid-numeric index is provided.
     *
     * @param indices The indices of the items to be deleted. \
     * A negative index is normalized as following: \
     * - If the index is less than negative of list-{@link length} it's treated as 0.
     * - Otherwise, it's subtracted from the list-{@link length}.
     * eg: If list length is `5`, negative index `-5` will be normalized to `0`, \
     * negative index `-6` will be normalized to `0`, and so on.
     * @returns The removed items or an empty `array`.
     *
     * @triggers {@link EventListUnitDelete}
     * @category Custom ListUnit
     */
    delete(...indices) {
        var _a;
        indices = sanitizeIndices(indices, this.length).filter(i => this.hasOwnProperty(i));
        if (this.isFrozen || indices.length === 0 || this.isEmpty) {
            return [];
        }
        const deletedItems = [];
        const listShallowCopy = [...this.rawValue()];
        indices.forEach(index => {
            deletedItems.push(this.deepCopyMaybe(listShallowCopy[index]));
            delete listShallowCopy[index];
        });
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitDelete(indices, deletedItems));
        }
        return deletedItems;
    }
    /**
     * Deletes items from the list-{@link value} where the predicate returns `true`. \
     * Dispatches a new copy of the value, without mutating the current-value. \
     * It doesn't modify the length of the list as these indices are only deleted, not removed.
     *
     * It only works if the Unit is not frozen, and
     * the predicate is a function, and the list is not empty.
     *
     * @param predicate A callback function that gets called for every item in the list with the item and index as arguments.
     * @returns The removed items or an empty `array`.
     *
     * @triggers {@link EventListUnitDelete}
     * @category Custom ListUnit
     */
    deleteIf(predicate) {
        if (this.isFrozen || typeof predicate !== 'function' || this.isEmpty) {
            return [];
        }
        const indicesToBeDeleted = [];
        this.value().forEach((item, index) => {
            if (predicate(item, index)) {
                indicesToBeDeleted.push(index);
            }
        });
        return this.delete(...indicesToBeDeleted);
    }
    /**
     * Finds items of the list that have a direct child property
     * that matches with `key` and `value` passed as params.
     *
     * @example
     * ```ts
     * const initialValue = [{b: 1}, {b: 1, b2: 2}, {c: 1}, {b: null}];
     * const list = new ListUnit({initialValue});
     * const itemsFound = list.findByProp('b', 1);
     * console.log(itemsFound); // logs [[0, {b: 1}], [1, {b: 1, b2: 2}]]
     * ```
     *
     * @param key The key of the property whose value needs to be matched against param `value`.
     * @param value A value that will be matched against every item's prop-value using equality operator.
     * @param strictEquality A flag governing whether the value be matched using `===` or `==` operator.
     * Default is `true`, ie: strict equality `===`. Pass `false` to use `==` instead.
     * @returns An `array` of key/values of the matched properties, an empty `array` otherwise.
     *
     * @category Custom ListUnit
     */
    findByProp(key, value, strictEquality = true) {
        if (this.isEmpty) {
            return [];
        }
        return this.rawValue().reduce((res, item, index) => {
            const aMatch = isObject(item) &&
                (strictEquality === false
                    ? // tslint:disable-next-line:triple-equals
                        item[key] == value
                    : item[key] === value);
            if (aMatch) {
                res.push([index, this.deepCopyMaybe(item)]);
            }
            return res;
        }, []);
    }
    /**
     * Returns the item at the given index in the list-{@link value}. \
     * Negative index returns items from the end of the list.
     *
     * @param index The index of the item. \
     * A negative index is normalized as following: \
     * - If the index is less than negative of list-{@link length} it's treated as 0.
     * - Otherwise, it's subtracted from the list-{@link length}.
     * eg: If list length is `5`, negative index `-5` will be normalized to `0`, \
     * negative index `-6` will be normalized to `0`, and so on.
     * @returns The item at the given index.
     *
     * @category Custom ListUnit
     */
    get(index) {
        index = normalizeIndex(index, this.length);
        return this.hasOwnProperty(index) ? this.deepCopyMaybe(this.rawValue()[index]) : undefined;
    }
    /**
     * Returns the first item in the list-{@link value}.
     *
     * @returns The first item in the list.
     *
     * @category Custom ListUnit
     */
    first() {
        return this.get(0);
    }
    /**
     * Returns the last item in the list-{@link value}.
     *
     * @returns The last item in the list.
     *
     * @category Custom ListUnit
     */
    last() {
        return this.get(-1);
    }
    /**
     * Adds all the items of the list separated by the specified separator string, and
     * all the items are converted into string first using JSON.stringify
     *
     * @param separator A string used to separate one item of the list from the next in the resulting String.
     * If omitted, the list items are separated with a comma.
     * @returns Concatenated `JSON.stringify`d list items.
     *
     * @category Custom ListUnit
     */
    jsonJoin(separator) {
        return this.rawValue()
            .map(item => JSON.stringify(item))
            .join(separator);
    }
    /**
     * Appends new items to the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and at least 1 item is provided.
     *
     * @param items The items to be appended to the list.
     * @returns The new length of the list.
     *
     * @triggers {@link EventListUnitPush}
     * @category Customised Array.prototype
     */
    push(...items) {
        var _a;
        if (this.isFrozen || !items.length) {
            return this.length;
        }
        this.checkSerializabilityMaybe(items);
        const listShallowCopy = [...this.rawValue()];
        const newLength = listShallowCopy.push(...this.deepCopyMaybe(items));
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitPush(items));
        }
        return newLength;
    }
    /**
     * Removes the last item from the list-{@link value} and returns it. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the list is not empty {@link isEmpty}.
     *
     * @returns The popped item.
     *
     * @triggers {@link EventListUnitPop}
     * @category Customised Array.prototype
     */
    pop() {
        var _a;
        if (this.isFrozen || this.isEmpty) {
            return;
        }
        const listShallowCopy = [...this.rawValue()];
        const poppedItem = this.deepCopyMaybe(listShallowCopy.pop());
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitPop(poppedItem));
        }
        return poppedItem;
    }
    /**
     * Removes the first item from the list-{@link value} and returns it. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the list is not empty {@link isEmpty}.
     *
     * @returns The shifted item.
     *
     * @triggers {@link EventListUnitShift}
     * @category Customised Array.prototype
     */
    shift() {
        var _a;
        if (this.isFrozen || this.isEmpty) {
            return;
        }
        const listShallowCopy = [...this.rawValue()];
        const shiftedItem = this.deepCopyMaybe(listShallowCopy.shift());
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitShift(shiftedItem));
        }
        return shiftedItem;
    }
    /**
     * Inserts new items at the start of the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and at least 1 item is provided.
     *
     * @param items The items to be insert at the start of the list
     * @returns The new length of the list.
     *
     * @triggers {@link EventListUnitUnshift}
     * @category Customised Array.prototype
     */
    unshift(...items) {
        var _a;
        if (this.isFrozen || !items.length) {
            return this.length;
        }
        this.checkSerializabilityMaybe(items);
        const listShallowCopy = [...this.rawValue()];
        const newLength = listShallowCopy.unshift(...this.deepCopyMaybe(items));
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitUnshift(items));
        }
        return newLength;
    }
    /**
     * Removes items from the list-{@link value} and, if necessary,
     * inserts new items in their place, returning the deleted items. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and \
     * either deleteCount is not 0 (loose comparison) and the list is not empty {@link isEmpty}, or \
     * there's at least 1 item to be added.
     *
     * @param start The zero-based location in the list from which to start removing items.
     * @param deleteCount The number of items to remove.
     * @param items Items to insert into the list in place of the deleted items.
     * @returns The deleted Items or an empty `array`.
     *
     * @triggers {@link EventListUnitSplice}
     * @category Customised Array.prototype
     */
    splice(start, deleteCount, ...items) {
        var _a;
        // tslint:disable-next-line:triple-equals
        if (this.isFrozen || ((deleteCount == 0 || this.isEmpty) && !items.length)) {
            return [];
        }
        this.checkSerializabilityMaybe(items);
        const listShallowCopy = [...this.rawValue()];
        const removedItems = this.deepCopyMaybe(listShallowCopy.splice(start, deleteCount, ...items.map(item => this.deepCopyMaybe(item))));
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitSplice(start, deleteCount, removedItems, items));
        }
        return removedItems;
    }
    /**
     * Fills a section of the list-{@link value} with provided param `item`. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the list is not empty {@link isEmpty}.
     *
     * @param item The item to fill the section of ListUnit's value with.
     * @param start The index to start filling the list at. If start is negative, it is treated as
     * length+start where length is the length of the list.
     * @param end The index to stop filling the list at. If end is negative, it is treated as
     * length+end.
     * @returns Nothing, unlike `Array.prototype.fill`
     *
     * @triggers {@link EventListUnitFill}
     * @category Customised Array.prototype
     */
    fill(item, start, end) {
        var _a;
        if (this.isFrozen || this.isEmpty) {
            return;
        }
        this.checkSerializabilityMaybe(item);
        const listShallowCopy = [...this.rawValue()];
        listShallowCopy.fill(this.deepCopyMaybe(item), start, end);
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitFill(item, start, end));
        }
    }
    /**
     * Copies a section of the current-value identified by param `start` and param `end`
     * to the ListUnit's value starting at position {@link target}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the list is not empty {@link isEmpty}.
     *
     * @param target If target is negative, it is treated as length+target where length is the
     * length of the list.
     * @param start If start is negative, it is treated as length+start. If end is negative, it
     * is treated as length+end.
     * @param end If end is not specified, length of the list is used as its default value.
     * @returns Nothing, unlike `Array.prototype.copyWithin`
     *
     * @triggers {@link EventListUnitCopyWithin}
     * @category Customised Array.prototype
     */
    copyWithin(target, start, end) {
        var _a;
        if (this.isFrozen || this.isEmpty) {
            return;
        }
        const listShallowCopy = [...this.rawValue()];
        listShallowCopy.copyWithin(target, start, end);
        this.updateValueAndCache(listShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitCopyWithin(target, start, end));
        }
    }
    /**
     * Reverses the items in the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the list is not empty {@link isEmpty}.
     *
     * @returns Nothing, unlike `Array.prototype.reverse`
     *
     * @triggers {@link EventListUnitReverse}
     * @category Customised Array.prototype
     */
    reverse() {
        var _a;
        if (this.isFrozen || this.isEmpty) {
            return;
        }
        const listShallowCopyReversed = [...this.rawValue()].reverse();
        this.updateValueAndCache(listShallowCopyReversed);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitReverse());
        }
    }
    /**
     * Sorts the list-{@link value}. \
     * Dispatches a new copy of the value, without mutating the current-value.
     *
     * It only works if the Unit is not frozen, and the list is not empty {@link isEmpty}.
     *
     * @param compareFn The name of the function used to determine the order of the items.
     *        If omitted, the items are sorted in ascending, ASCII character order.
     * @returns Nothing, unlike `Array.prototype.sort`
     *
     * @triggers {@link EventListUnitSort}
     * @category Customised Array.prototype
     */
    sort(compareFn) {
        var _a;
        if (this.isFrozen || this.isEmpty) {
            return;
        }
        const listShallowCopySorted = typeof compareFn === 'function'
            ? [...this.value()].sort(compareFn)
            : [...this.rawValue()].sort();
        this.updateValueAndCache(listShallowCopySorted);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventListUnitSort());
        }
    }
    /**
     * Returns a section of the list-{@link value}.
     *
     * @param start The beginning of the specified portion of the list.
     * @param end The end of the specified portion of the list.
     * @returns A section of the list.
     *
     * @category Customised Array.prototype
     */
    slice(start, end) {
        return this.deepCopyMaybe(this.rawValue().slice(start, end));
    }
    /**
     * Performs the specified action for each item in the list-{@link value}. \
     * It's a drop-in replacement for the `Array.prototype.forEach` method.
     *
     * @param callbackFn A function that accepts up to three arguments.
     * forEvery calls the callbackFn function one time for each element in the list.
     * @param thisArg An object to which this keyword can refer in the callbackFn function.
     * If thisArg is omitted, undefined is used as this value.
     *
     * @category Customised Array.prototype
     */
    forEvery(callbackFn, thisArg) {
        Array.prototype.forEach.apply(this.value(), [callbackFn, thisArg]);
    }
    /**
     * @internal please do not use.
     */
    [IteratorSymbol]() {
        return Array.prototype[IteratorSymbol].call(this.value());
    }
    /**
     * @internal please do not use.
     */
    isValidValue(value) {
        return Array.isArray(value);
    }
}
/**
 * @internal please do not use.
 */
const MethodsWithNoRiskOfMutation = [
    'includes',
    'indexOf',
    'lastIndexOf',
    'join',
    'keys',
    'toLocaleString',
];
MethodsWithNoRiskOfMutation.forEach(methodName => {
    Object.defineProperty(ListUnit.prototype, methodName, {
        value(...args) {
            return Array.prototype[methodName].apply(this.rawValue(), args);
        },
    });
});
/**
 * @internal please do not use.
 */
const MethodsNotToImplement$3 = [
    ...Object.getOwnPropertyNames(Observable.prototype),
    ...Object.getOwnPropertyNames(Base.prototype),
    ...Object.getOwnPropertyNames(UnitBase.prototype),
    ...Object.getOwnPropertyNames(ListUnit.prototype),
    ...MethodsWithNoRiskOfMutation,
];
Object.getOwnPropertyNames(Array.prototype).forEach(method => {
    if (!isFunction(Array.prototype[method]) || MethodsNotToImplement$3.includes(method)) {
        return;
    }
    Object.defineProperty(ListUnit.prototype, method, {
        value(...args) {
            return Array.prototype[method].apply(this.value(), args);
        },
    });
});

/**
 * DictUnit is a reactive storage Unit that is loosely based on `Map`.
 *
 * It only accepts plain dictionary `object` as its value.
 * It ensures that at any point of time the value would always be a dictionary `object`.
 *
 * Learn more about Units [here](https://docs.activejs.dev/fundamentals/units). \
 * Learn more about DictUnit [here](https://docs.activejs.dev/fundamentals/units/dictunit).
 *
 * Just like every other Non-Primitive ActiveJS Unit:
 * - DictUnit extends {@link NonPrimitiveUnitBase}
 * - Which further extends {@link UnitBase}, {@link Base} and `Observable`
 *
 * @category 1. Units
 */
class DictUnit extends NonPrimitiveUnitBase {
    constructor(config) {
        super(Object.assign(Object.assign({}, Configuration.DICT_UNIT), config));
        makeNonEnumerable(this);
    }
    /**
     * The length of keys of the properties in the dictionary {@link value}.
     */
    get length() {
        return this.objectKeys().length;
    }
    /**
     * Indicates whether the length of the keys of the properties in the dictionary {@link value} is 0 or not.
     */
    get isEmpty() {
        return this.length === 0;
    }
    /**
     * @internal please do not use.
     */
    defaultValue() {
        return {};
    }
    /**
     * Extends {@link UnitBase.wouldDispatch} and adds additional check for Object dictionary {@link isDict},
     * which cannot be bypassed even by using {@link force}.
     *
     * @param value The value to be dispatched.
     * @param force Whether dispatch-checks should be bypassed or not.
     * @returns A boolean indicating whether the param `value` would pass the dispatch-checks if dispatched.
     *
     * @category Common Units
     */
    wouldDispatch(value, force) {
        return this.isValidValue(value) && super.wouldDispatch(value, force);
    }
    /**
     * Adds a property by setting given property-value on the given key in the dictionary {@link value}. \
     * Also, dispatches it as new dictionary, without mutating the current {@link value}.
     *
     * It only works if the Unit is not frozen and the `key` is either `string` or `number`.
     *
     * @param key The name of the property.
     * @param value The property-value.
     *
     * @triggers {@link EventDictUnitSet}
     * @category Custom DictUnit
     */
    set(key, value) {
        var _a;
        if (this.isFrozen || !isValidKey(key)) {
            return;
        }
        this.checkSerializabilityMaybe(value);
        const dictShallowCopy = Object.assign({}, this.rawValue());
        dictShallowCopy[key] = this.deepCopyMaybe(value);
        this.updateValueAndCache(dictShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventDictUnitSet(key, value));
        }
    }
    /**
     * Deletes properties with given keys from the dictionary value. See {@link value}. \
     * Deletion is performed on a copy of the value, which is then dispatched, without mutating the current value.
     *
     * It only works if the Unit is not frozen, and the value is not empty, \
     * and at least one of the passed keys exist in the value.
     * ie: at least 1 key value pair exists in the dictionary.
     *
     * @param keys The names of the properties to be removed.
     * @returns The removed properties or an empty `object-literal`.
     *
     * @triggers {@link EventDictUnitDelete}
     * @category Custom DictUnit
     */
    delete(...keys) {
        var _a;
        keys = keys.filter(key => this.has(key));
        if (this.isFrozen || this.isEmpty || !keys.length) {
            return {};
        }
        const dictShallowCopy = Object.assign({}, this.rawValue());
        const removedProps = {};
        keys.forEach(key => {
            removedProps[key] = this.deepCopyMaybe(dictShallowCopy[key]);
            delete dictShallowCopy[key];
        });
        this.updateValueAndCache(dictShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventDictUnitDelete(removedProps));
        }
        return removedProps;
    }
    /**
     * Deletes properties from the dictionary {@link value} where the predicate returns `true`. \
     * Also, dispatches it as new dictionary, without mutating the current {@link value}.
     *
     * It only works if the Unit is not frozen, and
     * the predicate is a function, and the dictionary is not empty.
     *
     * @param predicate A callback function that gets called for every property in the dictionary
     * with the property-value and key as arguments.
     * @returns The removed properties, or an empty `object-literal`.
     *
     * @triggers {@link EventDictUnitDelete}
     * @category Custom DictUnit
     */
    deleteIf(predicate) {
        var _a;
        if (this.isFrozen || typeof predicate !== 'function' || this.isEmpty) {
            return {};
        }
        const dictShallowCopy = Object.assign({}, this.rawValue());
        const removedProps = {};
        Object.keys(dictShallowCopy).forEach((key, index) => {
            if (predicate(this.deepCopyMaybe(dictShallowCopy[key]), key, index)) {
                removedProps[key] = dictShallowCopy[key];
                delete dictShallowCopy[key];
            }
        });
        this.updateValueAndCache(dictShallowCopy);
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventDictUnitDelete(removedProps));
        }
        return removedProps;
    }
    /**
     * Copy the values of all the enumerable own properties from one or more source objects to the dictionary. \
     * Also, dispatches it as new dictionary, without mutating the current {@link value}.
     *
     * It only works if the Unit is not frozen, and at least 1 source is provided, and it is a non-null object.
     * eg: `array`, `object`
     *
     * @param sources The source objects from which to copy properties.
     *
     * @triggers {@link EventDictUnitAssign}
     * @category Custom DictUnit
     */
    assign(...sources) {
        var _a;
        sources = sources.filter(isObject);
        if (this.isFrozen || !sources.length) {
            return;
        }
        this.checkSerializabilityMaybe(sources);
        const dictShallowCopy = Object.assign({}, this.rawValue());
        const newProps = Object.assign({}, ...sources);
        this.updateValueAndCache(Object.assign(dictShallowCopy, this.deepCopyMaybe(newProps)));
        if (((_a = this.eventsSubject) === null || _a === void 0 ? void 0 : _a.observers.length) && !this.isMuted) {
            this.eventsSubject.next(new EventDictUnitAssign(sources, newProps));
        }
    }
    /**
     * Returns the value of the property with the name equal to the given key in the dictionary {@link value}.
     *
     * It only returns own-properties, e.g.: key 'toString' would return `undefined`.
     *
     * @param key The name of the property.
     *
     * @category Custom DictUnit
     */
    get(key) {
        return this.hasOwnProperty(key) ? this.deepCopyMaybe(this.rawValue()[key]) : undefined;
    }
    /**
     * Returns whether a property with the given key exists in the dictionary {@link value}.
     *
     * @param key The name of the property.
     *
     * @category Custom DictUnit
     */
    has(key) {
        return this.hasOwnProperty(key);
    }
    /**
     * Finds direct properties of the dictionary that have a direct child property
     * that matches with `key` and `value` passed as params.
     *
     * @example
     * ```ts
     * const initialValue = {'first': {b: 1}, 'second': {b: 1, b2: 2}, 'third': {c: 1}, 'fourth': {b: null}};
     * const dict = new DictUnit({initialValue});
     * const itemsFound = dict.findByProp('b', 1);
     * console.log(itemsFound); // logs [['first', {b: 1}], ['second', {b: 1, b2: 2}]]
     * ```
     *
     * @param key The key of the property whose value needs to be matched against param `value`.
     * @param value A primitive value that will be matched against every item's prop-value using equality operator.
     * @param strictEquality A flag governing whether the value be matched using `===` or `==` operator.
     * Default is `true`, ie: strict equality `===`. Pass `false` to use `==` instead.
     * @returns An `array` of key/values of the matched properties, an empty `array` otherwise.
     *
     * @category Custom DictUnit
     */
    findByProp(key, value, strictEquality = true) {
        if (this.isEmpty) {
            return [];
        }
        return this.deepCopyMaybe(Object.entries(this.rawValue()).filter(([propKey, prop]) => isObject(prop) &&
            (strictEquality === false
                ? // tslint:disable-next-line:triple-equals
                    prop[key] == value
                : prop[key] === value)));
    }
    /**
     * Performs the specified action for each property in the object value. See {@link value}. \
     * It's a drop-in replacement for the `forEach` method.
     *
     * @param callbackFn A function that accepts up to two arguments.
     * forEvery calls the callbackFn function one time for each property in the object value.
     * @param thisArg An object to which this keyword can refer in the callbackFn function.
     * If thisArg is omitted, undefined is used as this value.
     *
     * @category Custom DictUnit
     */
    forEvery(callbackFn, thisArg) {
        this.objectEntries().forEach(([key, value], i, valueEntries) => callbackFn.call(thisArg, value, key, i, valueEntries));
    }
    /**
     * @internal please do not use.
     */
    [IteratorSymbol]() {
        let index = 0;
        const items = this.objectEntries();
        const length = items.length;
        return {
            next() {
                return { value: items[index++], done: index > length };
            },
        };
    }
    /**
     * @internal please do not use.
     */
    isValidValue(value) {
        return isDict(value);
    }
}

/**
 * @hidden
 * @experimental Meaning, It can disappear in the next version.
 *
 * An experimental short-hand function for Unit creation.
 * It automatically chooses an appropriate type of Unit depending on the
 * provided `initialValue`.
 *
 * For a `boolean` value, {@link BoolUnit} will be selected. \
 * For a `number` value, {@link NumUnit} will be selected. \
 * For a `string` value, {@link StringUnit} will be selected. \
 * For an `array` value, {@link ListUnit} will be selected. \
 * For a simple `dictionary object`, a {@link DictUnit} will be selected. \
 * For every other value, {@link GenericUnit} will be selected. \
 *
 * @param initialValue Initial value of the Unit.
 * @param config The configuration for the automatically selected Unit.
 */
function createUnit(initialValue, config) {
    const unitConfig = Object.assign(Object.assign({}, config), { initialValue });
    const typeOfValue = typeof initialValue;
    switch (true) {
        case initialValue == null:
        default:
            return new GenericUnit(unitConfig);
        case typeOfValue === 'boolean':
            return new BoolUnit(unitConfig);
        case typeOfValue === 'number':
            return new NumUnit(unitConfig);
        case typeOfValue === 'string':
            return new StringUnit(unitConfig);
        case Array.isArray(initialValue):
            return new ListUnit(unitConfig);
        case isDict(initialValue):
            return new DictUnit(unitConfig);
    }
}

/**
 * Base class for AsyncSystem.
 *
 * It can be used to create custom, {@link AsyncSystem} like Systems.
 *
 * See {@link https://docs.activejs.dev/fundamentals/systems/custom-asyncsystem} for more details.
 *
 * Just like every other ActiveJS construct:
 * - AsyncSystem extends {@link Base}
 * - Which further extends `Observable`
 *
 * @category 3. Systems
 */
class AsyncSystemBase extends Base {
    constructor(
    /**
     * The member Unit that is intended to portray the role of `query` aspect of an async task.
     * @category Member Units
     */
    queryUnit, 
    /**
     * The member Unit that is intended to portray the role of `response` aspect of an async task.
     * @category Member Units
     */
    dataUnit, 
    /**
     * The member Unit that is intended to portray the role of `error` aspect of an async task.
     * @category Member Units
     */
    errorUnit, 
    /**
     * The member Unit that is intended to portray the role of `pending-status` of an async task.
     * @category Member Units
     */
    pendingUnit, config) {
        super(config);
        this.queryUnit = queryUnit;
        this.dataUnit = dataUnit;
        this.errorUnit = errorUnit;
        this.pendingUnit = pendingUnit;
        /**
         * @internal please do not use.
         *
         * It works because all our Subjects and operations are synchronous.
         */
        this.relationshipsAutoPaused = false;
        this.relationshipsManuallyPaused = false;
        checkAsyncSystemConfig(config)();
        this.emit();
        this.createRelationshipsAmongMemberUnits();
        makeNonEnumerable(this);
    }
    /**
     * To check whether the inter-relationships among the member Units are active or not.
     *
     * @default `true`
     */
    get relationshipsWorking() {
        return !this.relationshipsAutoPaused && !this.relationshipsManuallyPaused;
    }
    /**
     * Combined value of all the member Units.
     *
     * @category Access Value
     */
    value() {
        const val = {
            query: this.queryUnit.value(),
            data: this.dataUnit.value(),
            error: this.errorUnit.value(),
            pending: this.pendingUnit.value(),
        };
        if (Configuration.ENVIRONMENT.checkImmutability === true) {
            Object.freeze(val);
        }
        return val;
    }
    /**
     * A helper method that creates a stream by subscribing to the Observable returned by the param `observableProducer` callback.
     *
     * Ideally the callback function creates an Observable by applying `Observable.pipe`
     * on the {@link queryUnit} or `queryUnit.future$` as source Observable.
     *
     * Then, after a successful data flow, dispatch the data to the {@link dataUnit}; \
     * and after a failure, dispatch the error to the {@link errorUnit}, caught by using RxJS' catchError operator.
     *
     * Just know that you should catch the error in a sub-pipe (ie: do not let it propagate to the main-pipe), otherwise
     * as usual the stream will stop working, and will not react on any further emissions.
     *
     * @param observableProducer A callback function that should return an Observable.
     *
     * @category Common
     */
    createStream(observableProducer) {
        const observable = observableProducer(this.queryUnit, this.dataUnit, this.errorUnit, this.pendingUnit);
        return new Stream(observable);
    }
    /**
     * To pause inter-relationships among the member Units. Also see {@link relationshipsWorking}.
     *
     * When inter-relationships are paused,
     * you can perform any number of operations on the member Units
     * without triggering the automatic relationships like {@link AsyncSystemBaseConfig.clearErrorOnData},
     * {@link AsyncSystemBaseConfig.autoUpdatePendingValue}, etc.
     *
     * This also means that the AsyncSystem stops emitting new values.
     *
     * @category Custom AsyncSystem
     */
    pauseRelationships() {
        if (this.relationshipsManuallyPaused === true) {
            return;
        }
        this.relationshipsManuallyPaused = true;
        this.unitsEmitCountsBeforePausing = this.unitsEmitCounts();
    }
    /**
     * To resume inter-relationships among the member Units. Also see {@link relationshipsWorking}.
     *
     * It restores the inter-relationships like {@link AsyncSystemBaseConfig.clearErrorOnData},
     * {@link AsyncSystemBaseConfig.autoUpdatePendingValue}, etc.
     *
     * This also means that the AsyncSystem starts emitting new values. \
     * And if any of the member Units emitted a value while the relationships were paused,
     * the AsyncSystem will emit a new value immediately to bring itself and its subscribers in sync
     * with the member Units.
     *
     * @category Custom AsyncSystem
     */
    resumeRelationships() {
        if (this.relationshipsManuallyPaused === false) {
            return;
        }
        this.relationshipsManuallyPaused = false;
        if (this.unitsEmitCountsBeforePausing.join() !== this.unitsEmitCounts().join()) {
            this.emit();
        }
    }
    /**
     * @internal please do not use.
     */
    unitsEmitCounts() {
        return [
            this.queryUnit.emitCount,
            this.dataUnit.emitCount,
            this.errorUnit.emitCount,
            this.pendingUnit.emitCount,
        ];
    }
    /**
     * @internal please do not use.
     */
    createRelationshipsAmongMemberUnits() {
        this.queryUnit.future$.subscribe(() => {
            if (this.relationshipsWorking) {
                this.executeQueryUnitRelationship();
            }
        });
        this.dataUnit.future$.subscribe(() => {
            if (this.relationshipsWorking) {
                this.executeDataUnitRelationship();
            }
        });
        this.errorUnit.future$.subscribe(() => {
            if (this.relationshipsWorking) {
                this.executeErrorUnitRelationship();
            }
        });
        this.pendingUnit.future$.subscribe(isPending => {
            if (!this.relationshipsManuallyPaused) {
                this.toggleQueryUnitFreezeMaybe(isPending);
            }
            if (this.relationshipsWorking) {
                this.emit();
            }
        });
    }
    /**
     * @internal please do not use.
     */
    executeQueryUnitRelationship() {
        this.relationshipsAutoPaused = true;
        this.autoUpdatePendingValue(true);
        if (this.config.clearDataOnQuery === true) {
            this.dataUnit.clearValue();
        }
        if (this.config.clearErrorOnQuery === true) {
            this.errorUnit.clearValue();
        }
        this.relationshipsAutoPaused = false;
        this.emit();
    }
    /**
     * @internal please do not use.
     */
    executeDataUnitRelationship() {
        this.relationshipsAutoPaused = true;
        this.autoUpdatePendingValue(false);
        if (this.config.clearErrorOnQuery !== true && this.config.clearErrorOnData !== false) {
            this.errorUnit.clearValue();
        }
        if (this.config.clearQueryOnData === true) {
            this.queryUnit.clearValue();
        }
        this.relationshipsAutoPaused = false;
        this.emit();
    }
    /**
     * @internal please do not use.
     */
    executeErrorUnitRelationship() {
        this.relationshipsAutoPaused = true;
        this.autoUpdatePendingValue(false);
        if (this.config.clearDataOnQuery !== true && this.config.clearDataOnError === true) {
            this.dataUnit.clearValue();
        }
        if (this.config.clearQueryOnError === true) {
            this.queryUnit.clearValue();
        }
        this.relationshipsAutoPaused = false;
        this.emit();
    }
    /**
     * @internal please do not use.
     */
    toggleQueryUnitFreezeMaybe(isPending) {
        if (this.config.freezeQueryWhilePending === true) {
            if (isPending) {
                this.queryUnit.freeze();
            }
            else {
                this.queryUnit.unfreeze();
            }
        }
    }
    /**
     * @internal please do not use.
     */
    autoUpdatePendingValue(isPending) {
        if (this.config.autoUpdatePendingValue !== false) {
            this.pendingUnit.dispatch(isPending, { bypassDebounce: true });
        }
    }
    /**
     * @internal please do not use.
     */
    emit(value = this.combinedEmittedValues()) {
        if (Configuration.ENVIRONMENT.checkImmutability === true) {
            Object.freeze(value);
        }
        super.emit(value);
    }
    /**
     * @internal please do not use.
     */
    combinedEmittedValues() {
        // tslint:disable:no-string-literal
        return {
            query: this.queryUnit['emittedValue'],
            data: this.dataUnit['emittedValue'],
            error: this.errorUnit['emittedValue'],
            pending: this.pendingUnit['emittedValue'],
        };
        // tslint:enable:no-string-literal
    }
}

/**
 * AsyncSystem is a reactive storage System, to store, replay and wait for simple async tasks.
 *
 * An AsyncSystem is a systematic combination of four Units.
 * one each for every aspect of an asynchronous task or API, \
 * e.g.: XHR, fetch or a third party abstraction like Angular's HttpClient.
 *
 * See {@link https://docs.activejs.dev/fundamentals/systems/asyncsystem} for more details.
 *
 * - AsyncSystem extends {@link AsyncSystemBase},
 *   which further extends {@link Base} and `Observable`
 *
 * @category 3. Systems
 */
class AsyncSystem extends AsyncSystemBase {
    constructor(config) {
        super(...(() => {
            config = Object.assign(Object.assign({}, Configuration.ASYNC_SYSTEM), config);
            const { id: systemId, initialValue, QUERY_UNIT, DATA_UNIT, ERROR_UNIT, PENDING_UNIT, UNITS, } = config;
            const { queryUnitId, dataUnitId, errorUnitId, pendingUnitId } = generateAsyncSystemIds(systemId, QUERY_UNIT, DATA_UNIT, ERROR_UNIT, PENDING_UNIT);
            // no need to check these, as Units ignore undefined as initialValue anyway
            const { query, data, error, pending } = initialValue || {};
            const queryUnit = new GenericUnit(Object.assign(Object.assign(Object.assign({ initialValue: query }, UNITS), QUERY_UNIT), { id: queryUnitId }));
            const dataUnit = new GenericUnit(Object.assign(Object.assign(Object.assign({ initialValue: data }, UNITS), DATA_UNIT), { id: dataUnitId }));
            const errorUnit = new GenericUnit(Object.assign(Object.assign(Object.assign({ initialValue: error }, UNITS), ERROR_UNIT), { id: errorUnitId }));
            const pendingUnit = new BoolUnit(Object.assign(Object.assign(Object.assign({ initialValue: pending }, UNITS), PENDING_UNIT), { id: pendingUnitId }));
            return [queryUnit, dataUnit, errorUnit, pendingUnit, config];
        })());
    }
}

/**
 * A Cluster is just a wrapper, a group, of two or more ActiveJS fundamental constructs, `Units`, `Systems`, `Actions`, or even `Clusters`.
 *
 * It creates a master `Observable` of the combined value of its members by merging all the provided Observable constructs.
 * Whenever any of these wrapped constructs emits a value, `Cluster` emits a new combined-value.
 *
 * See {@link https://docs.activejs.dev/fundamentals/cluster} for more details.
 *
 * @category 4. Utility
 */
class Cluster extends Base {
    constructor(items, config) {
        super(Object.assign(Object.assign({}, Configuration.CLUSTER), config));
        /**
         * The items part of this cluster, stored as key-value pairs.
         */
        this.items = {};
        checkClusterItems(items);
        this.extractItems(items);
        this.startListeningAndEmitting();
        makeNonEnumerable(this);
        Object.freeze(this.items);
    }
    /**
     * The count of items part of this cluster.
     */
    get itemsCount() {
        return this._itemsCount;
    }
    // tslint:enable:variable-name
    /**
     * Combined value of the items part of this Cluster.
     *
     * @category Access Value
     */
    value() {
        const val = this.itemsKeys.reduce((reduced, key) => {
            reduced[key] = this.items[key].value();
            return reduced;
        }, {});
        if (Configuration.ENVIRONMENT.checkImmutability === true) {
            Object.freeze(val);
        }
        return val;
    }
    /**
     * A helper method that creates a stream by subscribing to the Observable returned by the param `observableProducer` callback.
     *
     * Ideally the callback function creates an Observable by applying `Observable.pipe`.
     *
     * Just know that you should catch the error in a sub-pipe (ie: do not let it propagate to the main-pipe), otherwise
     * as usual the stream will stop working, and will not react on any further emissions.
     *
     * @param observableProducer A callback function that should return an Observable.
     *
     * @category Common
     */
    createStream(observableProducer) {
        const observable = observableProducer(this);
        return new Stream(observable);
    }
    /**
     * Select a child by providing its key.
     *
     * @param key The key of the child.
     *
     * @category Custom Cluster
     */
    select(key) {
        return this.items[key];
    }
    /**
     * Performs the specified action for each child of the Cluster {@link items}. \
     * It's a drop-in replacement for the `forEach` method.
     *
     * @param callbackFn A function that accepts up to three arguments.
     * forEvery calls the callbackFn function one time for each element in the list.
     * @param thisArg An object to which this keyword can refer in the callbackFn function.
     * If thisArg is omitted, undefined is used as this value.
     *
     * @category Custom Cluster
     */
    forEvery(callbackFn, thisArg) {
        Object.entries(this.items).forEach(([key, item], i, itemsAsEntries) => callbackFn.call(thisArg, item, key, i, itemsAsEntries));
    }
    /**
     * @internal please do not use.
     */
    [IteratorSymbol]() {
        let index = 0;
        const items = Object.entries(this.items);
        const length = items.length;
        return {
            next() {
                return { value: items[index++], done: index > length };
            },
        };
    }
    /**
     * @internal please do not use.
     */
    extractItems(items) {
        this.itemsKeys = Object.keys(items).filter(key => items[key] instanceof Base);
        this._itemsCount = this.itemsKeys.length;
        this.itemsKeys.forEach(key => {
            this.items[key] = items[key];
        });
    }
    /**
     * @internal please do not use.
     */
    startListeningAndEmitting() {
        this.emit();
        merge(...Object.values(this.items).map(item => item.future$)).subscribe(() => this.emit());
    }
    /**
     * @internal please do not use.
     */
    emit(value = this.combinedEmittedValues()) {
        if (Configuration.ENVIRONMENT.checkImmutability === true) {
            Object.freeze(value);
        }
        super.emit(value);
    }
    /**
     * @internal please do not use.
     */
    combinedEmittedValues() {
        return this.itemsKeys.reduce((reduced, key) => {
            // tslint:disable-next-line:no-string-literal
            reduced[key] = this.items[key]['emittedValue'];
            return reduced;
        }, {});
    }
}

/**
 * An Action is nothing, but an elaborate form of an RxJS Subject. \
 * Or in other words, a simplified form of Unit.
 *
 * Unlike Units, it doesn't perform any checks on dispatch. \
 * All values pass through, just like a Subject.
 *
 * See {@link https://docs.activejs.dev/fundamentals/action} for more details.
 *
 * @category 4. Utility
 */
class Action extends Base {
    constructor(config) {
        super(Object.assign(Object.assign({ replay: false }, Configuration.ACTION), config));
        this.dispatch(this.config.initialValue);
        makeNonEnumerable(this);
    }
    // tslint:enable:variable-name
    /**
     * Current value of the Action, the last dispatched value.
     *
     * @default `undefined`
     * @category Access Value
     */
    value() {
        return this._value;
    }
    /**
     * A helper method that creates a stream by subscribing to the Observable returned by the param `observableProducer` callback.
     *
     * Ideally the callback function creates an Observable by applying `Observable.pipe`.
     *
     * Just know that you should catch the error in a sub-pipe (ie: do not let it propagate to the main-pipe), otherwise
     * as usual the stream will stop working, and will not react on any further emissions.
     *
     * @param observableProducer A callback function that should return an Observable.
     *
     * @category Common
     */
    createStream(observableProducer) {
        const observable = observableProducer(this);
        return new Stream(observable);
    }
    dispatch(valueOrProducer) {
        this._value =
            typeof valueOrProducer === 'function'
                ? valueOrProducer(this.value())
                : valueOrProducer;
        if (Configuration.ENVIRONMENT.checkImmutability === true) {
            deepFreeze(this._value);
        }
        this.emit();
    }
}

/*
 * Public API Surface of ActiveJS
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Action, AsyncSystem, AsyncSystemBase, Base, BoolUnit, Cluster, Configuration, DictUnit, DispatchFailReason, EventDictUnitAssign, EventDictUnitDelete, EventDictUnitSet, EventListUnitCopyWithin, EventListUnitDelete, EventListUnitFill, EventListUnitPop, EventListUnitPush, EventListUnitRemove, EventListUnitReverse, EventListUnitSet, EventListUnitShift, EventListUnitSort, EventListUnitSplice, EventListUnitUnshift, EventReplay, EventUnitClear, EventUnitClearCache, EventUnitClearPersistedValue, EventUnitClearValue, EventUnitDispatch, EventUnitDispatchFail, EventUnitFreeze, EventUnitJump, EventUnitReset, EventUnitResetValue, EventUnitUnfreeze, EventUnitUnmute, GenericUnit, ListUnit, NonPrimitiveUnitBase, NumUnit, Selection, Stream, StringUnit, UnitBase, clearPersistentStorage, createUnit, deepCopy };
//# sourceMappingURL=activejs-core.js.map
